import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { sampleTerrainRadius } from '../terrain'
import { findVillageSites, findDockSites } from './findSites'
import { setVillagePlacements } from '@/interaction/InteractionDetector'
import { calculateDayFactor } from '@/utils/lighting'
import { STRUCTURE_INFO } from './types'
import type { VillagePlacement, Structure, PathSegment, Prop } from './types'

const DEG2RAD = Math.PI / 180

// ─── Seeded RNG ──────────────────────────────────────────────────────
function mulberry32(seed: number) {
  let s = seed | 0
  return () => {
    s = (s + 0x6D2B79F5) | 0
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// ─── Local-to-world projection ───────────────────────────────────────
function localToWorld(
  village: VillagePlacement,
  localX: number,
  localZ: number,
): { position: THREE.Vector3; normal: THREE.Vector3 } {
  const worldOffset = new THREE.Vector3()
    .addScaledVector(village.tangentX, localX)
    .addScaledVector(village.tangentZ, localZ)

  const dir = village.position.clone().add(worldOffset).normalize()
  const r = sampleTerrainRadius(dir.x, dir.y, dir.z)
  const position = dir.multiplyScalar(r)
  const normal = position.clone().normalize()

  return { position, normal }
}

// ─── Color helpers ───────────────────────────────────────────────────
function hexToRGB(hex: string): [number, number, number] {
  const n = parseInt(hex.replace('#', ''), 16)
  return [(n >> 16 & 0xff) / 255, (n >> 8 & 0xff) / 255, (n & 0xff) / 255]
}

/** Jitter a color by +-amount (0-1 scale). Deterministic from seed. */
function jitterColor(
  base: [number, number, number],
  amount: number,
  seed: number,
): [number, number, number] {
  // Simple hash for deterministic jitter
  const h = Math.sin(seed * 127.1 + 311.7) * 43758.5453
  const j = (h - Math.floor(h)) * 2 - 1 // -1 to 1
  const f = 1 + j * amount
  return [
    Math.min(1, Math.max(0, base[0] * f)),
    Math.min(1, Math.max(0, base[1] * f)),
    Math.min(1, Math.max(0, base[2] * f)),
  ]
}

/** Darken color toward base (rain splash weathering). t=0 full dark, t=1 no change */
function weatherDarken(
  base: [number, number, number],
  t: number,
  darkAmount: number = 0.2,
): [number, number, number] {
  const f = 1 - darkAmount * (1 - Math.min(1, Math.max(0, t)))
  return [base[0] * f, base[1] * f, base[2] * f]
}

/** Per-building color variation: slight hue/value shift so no two buildings are identical */
function varyColor(base: [number, number, number], seed: number, amount: number = 0.06): [number, number, number] {
  const h1 = Math.sin(seed * 78.233 + 12.9898) * 43758.5453
  const h2 = Math.sin(seed * 43.758 + 53.127) * 29463.1234
  const j1 = (h1 - Math.floor(h1)) * 2 - 1
  const j2 = (h2 - Math.floor(h2)) * 2 - 1
  return [
    Math.min(1, Math.max(0, base[0] * (1 + j1 * amount))),
    Math.min(1, Math.max(0, base[1] * (1 + (j1 * 0.7 + j2 * 0.3) * amount))),
    Math.min(1, Math.max(0, base[2] * (1 + j2 * amount * 0.8))),
  ]
}

/** Apply ambient occlusion factor to a color */
function aoColor(c: [number, number, number], ao: number): [number, number, number] {
  return [c[0] * ao, c[1] * ao, c[2] * ao]
}

// ─── Vertex builder ──────────────────────────────────────────────────
// All geometry is constructed from raw vertices with vertex colors.
// addBox: 8 vertices, 6 faces (12 triangles)
// addPrism: N-sided prism from vertices
// addWedge: triangular prism for gabled roofs

interface GeoArrays {
  positions: number[]
  colors: number[]
  indices: number[]
}

function createGeo(): GeoArrays {
  return { positions: [], colors: [], indices: [] }
}

function addBox(
  g: GeoArrays,
  center: THREE.Vector3,
  up: THREE.Vector3,
  right: THREE.Vector3,
  forward: THREE.Vector3,
  hw: number, hh: number, hd: number,
  color: [number, number, number],
  colorJitter: number = 0, // +-% brightness variation per vertex (0.05 = 5%)
  seed: number = 0,
) {
  const baseIdx = g.positions.length / 3
  let vi = 0
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      for (const sz of [-1, 1]) {
        g.positions.push(
          center.x + right.x * hw * sx + up.x * hh * sy + forward.x * hd * sz,
          center.y + right.y * hw * sx + up.y * hh * sy + forward.y * hd * sz,
          center.z + right.z * hw * sx + up.z * hh * sy + forward.z * hd * sz,
        )
        const c = colorJitter > 0 ? jitterColor(color, colorJitter, seed + vi * 7.31) : color
        g.colors.push(c[0], c[1], c[2])
        vi++
      }
    }
  }
  const b = baseIdx
  const faces = [
    [0, 1, 2, 3], [4, 6, 5, 7],
    [0, 4, 1, 5], [2, 3, 6, 7],
    [0, 2, 4, 6], [1, 5, 3, 7],
  ]
  for (const [a, bb, c, d] of faces) {
    g.indices.push(b + a, b + bb, b + c, b + bb, b + d, b + c)
  }
}

/** Triangular prism for gabled roof: ridge along `forward` direction,
 *  two sloped faces, two triangular end-caps. */
function addRoofWedge(
  g: GeoArrays,
  base: THREE.Vector3,    // center of base rectangle
  up: THREE.Vector3,      // surface normal / up
  right: THREE.Vector3,   // across the gable
  forward: THREE.Vector3, // along the ridge
  halfWidth: number,
  ridgeHeight: number,
  halfDepth: number,
  color: [number, number, number],
) {
  // 6 vertices: 4 at base rectangle, 2 at ridge
  const b = g.positions.length / 3

  // Base corners: BL, BR, FL, FR
  const bl = base.clone().add(right.clone().multiplyScalar(-halfWidth)).add(forward.clone().multiplyScalar(-halfDepth))
  const br = base.clone().add(right.clone().multiplyScalar(halfWidth)).add(forward.clone().multiplyScalar(-halfDepth))
  const fl = base.clone().add(right.clone().multiplyScalar(-halfWidth)).add(forward.clone().multiplyScalar(halfDepth))
  const fr = base.clone().add(right.clone().multiplyScalar(halfWidth)).add(forward.clone().multiplyScalar(halfDepth))
  // Ridge points
  const rb = base.clone().add(up.clone().multiplyScalar(ridgeHeight)).add(forward.clone().multiplyScalar(-halfDepth))
  const rf = base.clone().add(up.clone().multiplyScalar(ridgeHeight)).add(forward.clone().multiplyScalar(halfDepth))

  for (const v of [bl, br, fl, fr, rb, rf]) {
    g.positions.push(v.x, v.y, v.z)
    g.colors.push(color[0], color[1], color[2])
  }

  // Vertex indices: 0=bl, 1=br, 2=fl, 3=fr, 4=rb(ridge back), 5=rf(ridge front)
  // Left slope: bl(0) -> fl(2) -> rf(5) -> rb(4)
  g.indices.push(b + 0, b + 2, b + 5, b + 0, b + 5, b + 4)
  // Right slope: br(1) -> rb(4) -> rf(5) -> fr(3)
  g.indices.push(b + 1, b + 4, b + 5, b + 1, b + 5, b + 3)
  // Back gable triangle: bl(0) -> rb(4) -> br(1)
  g.indices.push(b + 0, b + 4, b + 1)
  // Front gable triangle: fl(2) -> fr(3) -> rf(5)
  g.indices.push(b + 2, b + 3, b + 5)
  // Bottom: bl(0) -> br(1) -> fr(3) -> fl(2)
  g.indices.push(b + 0, b + 1, b + 3, b + 0, b + 3, b + 2)
}

/** N-sided prism (cylinder approximation). Axis along `up`. */
function addPrism(
  g: GeoArrays,
  center: THREE.Vector3,
  up: THREE.Vector3,
  right: THREE.Vector3,
  forward: THREE.Vector3,
  radiusBottom: number,
  radiusTop: number,
  halfHeight: number,
  sides: number,
  color: [number, number, number],
) {
  const b = g.positions.length / 3

  // Bottom ring, then top ring
  for (let ring = 0; ring < 2; ring++) {
    const r = ring === 0 ? radiusBottom : radiusTop
    const yOff = ring === 0 ? -1 : 1
    for (let i = 0; i < sides; i++) {
      const angle = (i / sides) * Math.PI * 2
      const c = Math.cos(angle)
      const s = Math.sin(angle)
      const px = center.x + right.x * r * c + forward.x * r * s + up.x * halfHeight * yOff
      const py = center.y + right.y * r * c + forward.y * r * s + up.y * halfHeight * yOff
      const pz = center.z + right.z * r * c + forward.z * r * s + up.z * halfHeight * yOff
      g.positions.push(px, py, pz)
      g.colors.push(color[0], color[1], color[2])
    }
  }

  // Side faces
  for (let i = 0; i < sides; i++) {
    const i2 = (i + 1) % sides
    const b0 = b + i, b1 = b + i2, t0 = b + sides + i, t1 = b + sides + i2
    g.indices.push(b0, t1, b1, b0, t0, t1)
  }

  // Top cap
  const topCenter = g.positions.length / 3
  g.positions.push(
    center.x + up.x * halfHeight,
    center.y + up.y * halfHeight,
    center.z + up.z * halfHeight,
  )
  g.colors.push(color[0], color[1], color[2])
  for (let i = 0; i < sides; i++) {
    g.indices.push(topCenter, b + sides + (i + 1) % sides, b + sides + i)
  }

  // Bottom cap
  const botCenter = g.positions.length / 3
  g.positions.push(
    center.x - up.x * halfHeight,
    center.y - up.y * halfHeight,
    center.z - up.z * halfHeight,
  )
  g.colors.push(color[0], color[1], color[2])
  for (let i = 0; i < sides; i++) {
    g.indices.push(botCenter, b + i, b + (i + 1) % sides)
  }
}

/** Cone on a prism base (for tower tops, haystacks). */
function addCone(
  g: GeoArrays,
  center: THREE.Vector3,
  up: THREE.Vector3,
  right: THREE.Vector3,
  forward: THREE.Vector3,
  radius: number,
  halfHeight: number,
  sides: number,
  color: [number, number, number],
) {
  addPrism(g, center, up, right, forward, radius, 0.001, halfHeight, sides, color)
}

// ─── Building basis helper ───────────────────────────────────────────
/** Compute world position, orientation basis (up/right/forward), and
 *  apply a subtle random lean for visual imperfection. */
function buildingBasis(village: VillagePlacement, structure: { localX: number; localZ: number; rotation: number }, seed: number) {
  const { position, normal } = localToWorld(village, structure.localX, structure.localZ)
  const rng = mulberry32(seed)

  // Base orientation: align up with surface normal, then rotate by yaw
  let upVec = normal.clone()
  const arbitrary = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
  let rightVec = new THREE.Vector3().crossVectors(upVec, arbitrary).normalize()
  let forwardVec = new THREE.Vector3().crossVectors(rightVec, upVec).normalize()

  // Apply yaw rotation
  const cosY = Math.cos(structure.rotation)
  const sinY = Math.sin(structure.rotation)
  const newRight = rightVec.clone().multiplyScalar(cosY).add(forwardVec.clone().multiplyScalar(sinY))
  const newForward = forwardVec.clone().multiplyScalar(cosY).sub(rightVec.clone().multiplyScalar(sinY))
  rightVec = newRight.normalize()
  forwardVec = newForward.normalize()

  // Apply slight lean (0.5-1.5 degrees)
  const leanAngle = (0.5 + rng() * 1.0) * DEG2RAD
  const leanDir = rng() * Math.PI * 2
  const leanAxis = rightVec.clone().multiplyScalar(Math.cos(leanDir))
    .add(forwardVec.clone().multiplyScalar(Math.sin(leanDir))).normalize()
  const leanQ = new THREE.Quaternion().setFromAxisAngle(leanAxis, leanAngle)
  upVec.applyQuaternion(leanQ).normalize()
  rightVec.applyQuaternion(leanQ).normalize()
  forwardVec.applyQuaternion(leanQ).normalize()

  return { position, upVec, rightVec, forwardVec, rng }
}

// ─── Building geometry builders ──────────────────────────────────────

function buildHouse(g: GeoArrays, village: VillagePlacement, structure: Structure) {
  const info = STRUCTURE_INFO.house
  const seed = structure.localX * 137 + structure.localZ * 251
  const { position: pos, upVec: up, rightVec: right, forwardVec: fwd, rng } =
    buildingBasis(village, structure, seed)

  const hw = info.width / 2
  const hd = info.depth / 2
  const wallH = info.wallHeight
  const foundH = 0.3
  const roofPitch = info.roofPitch * DEG2RAD
  const roofRise = Math.tan(roofPitch) * hw
  const roofOverhang = 0.4 // larger overhang to protect wattle-and-daub
  const wallCol = varyColor(hexToRGB(info.wallColor), seed, 0.06)
  const roofCol = varyColor(hexToRGB(info.roofColor), seed + 1, 0.05)
  const trimCol = varyColor(hexToRGB(info.trimColor), seed + 2, 0.04)
  const stoneCol = varyColor([0.44, 0.42, 0.40] as [number, number, number], seed + 3, 0.05)
  const darkCol: [number, number, number] = [0.10, 0.08, 0.06]
  const doorCol: [number, number, number] = [0.42, 0.29, 0.16]
  const sillCol: [number, number, number] = [0.50, 0.48, 0.44]
  const wallColDark = weatherDarken(wallCol, 0.3, 0.15)

  // ── Foundation (plinth) ──
  // Slightly wider step at ground level, darker stone + ground AO
  const foundCenter = pos.clone().addScaledVector(up, foundH / 2)
  addBox(g, foundCenter, up, right, fwd, hw + 0.18, foundH / 2, hd + 0.18, aoColor(stoneCol, 0.6), 0.08, seed)

  // ── Walls with weathering ──
  // Lower half: darker (rain splash), upper half: normal
  const wallLowerH = wallH * 0.4
  const wallUpperH = wallH * 0.6
  const wallLowerC = pos.clone().addScaledVector(up, foundH + wallLowerH / 2)
  addBox(g, wallLowerC, up, right, fwd, hw, wallLowerH / 2, hd, wallColDark, 0.05, seed + 1)
  const wallUpperC = pos.clone().addScaledVector(up, foundH + wallLowerH + wallUpperH / 2)
  addBox(g, wallUpperC, up, right, fwd, hw, wallUpperH / 2, hd, wallCol, 0.04, seed + 2)

  // ── Half-timber frame ──
  // Vertical studs on long walls (close studding pattern)
  const numStuds = 6
  for (let i = 0; i < numStuds; i++) {
    const t = -1 + (2 * (i + 0.5)) / numStuds
    const studZ = t * (hd - 0.3)
    for (const side of [-1, 1]) {
      const tc = pos.clone()
        .addScaledVector(up, foundH + wallH / 2)
        .addScaledVector(right, side * (hw + 0.02))
        .addScaledVector(fwd, studZ)
      addBox(g, tc, up, right, fwd, 0.055, wallH / 2, 0.07, trimCol)
    }
  }

  // Horizontal sill beam at base of walls
  for (const side of [-1, 1]) {
    const sillC = pos.clone()
      .addScaledVector(up, foundH + 0.06)
      .addScaledVector(right, side * (hw + 0.02))
    addBox(g, sillC, up, right, fwd, 0.055, 0.06, hd, trimCol)
  }

  // Horizontal eaves beam (bressummer)
  for (const side of [-1, 1]) {
    const beamC = pos.clone()
      .addScaledVector(up, foundH + wallH)
      .addScaledVector(right, side * (hw + 0.02))
    addBox(g, beamC, up, right, fwd, 0.06, 0.08, hd + 0.05, trimCol)
  }

  // Mid-rail (horizontal beam at mid-wall height)
  for (const side of [-1, 1]) {
    const midC = pos.clone()
      .addScaledVector(up, foundH + wallH * 0.5)
      .addScaledVector(right, side * (hw + 0.02))
    addBox(g, midC, up, right, fwd, 0.055, 0.05, hd, trimCol)
  }

  // Corner posts (slightly larger)
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const cornerC = pos.clone()
        .addScaledVector(up, foundH + wallH / 2)
        .addScaledVector(right, sx * hw)
        .addScaledVector(fwd, sz * hd)
      addBox(g, cornerC, up, right, fwd, 0.08, wallH / 2, 0.08, trimCol)
    }
  }

  // Diagonal braces at corners (simplified: small angled boxes)
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const braceC = pos.clone()
        .addScaledVector(up, foundH + wallH * 0.25)
        .addScaledVector(right, sx * (hw + 0.02))
        .addScaledVector(fwd, sz * (hd - 0.5))
      addBox(g, braceC, up, right, fwd, 0.04, wallH * 0.2, 0.35, trimCol)
    }
  }

  // Gable-end timber framing (on short walls)
  for (const side of [-1, 1]) {
    // Vertical center post on gable
    const gablePostC = pos.clone()
      .addScaledVector(up, foundH + wallH / 2)
      .addScaledVector(fwd, side * (hd + 0.02))
    addBox(g, gablePostC, up, fwd, right, 0.055, wallH / 2, 0.07, trimCol)

    // Horizontal beam on gable
    const gableBeamC = pos.clone()
      .addScaledVector(up, foundH + wallH)
      .addScaledVector(fwd, side * (hd + 0.02))
    addBox(g, gableBeamC, up, fwd, right, 0.06, 0.08, hw, trimCol)
  }

  // ── Exposed rafter tails ──
  // Small boxes protruding under eaves on long walls
  const rafterSpacing = 0.8
  const numRafters = Math.floor(info.depth / rafterSpacing)
  for (let i = 0; i < numRafters; i++) {
    const rz = -hd + (i + 0.5) * rafterSpacing
    for (const side of [-1, 1]) {
      const rc = pos.clone()
        .addScaledVector(up, foundH + wallH - 0.05)
        .addScaledVector(right, side * (hw + roofOverhang * 0.5))
        .addScaledVector(fwd, rz)
      addBox(g, rc, up, right, fwd, roofOverhang * 0.35, 0.04, 0.035, trimCol)
    }
  }

  // ── Roof (wedge) with thatch ridge cap ──
  const roofBase = pos.clone().addScaledVector(up, foundH + wallH)
  addRoofWedge(g, roofBase, up, right, fwd, hw + roofOverhang, roofRise, hd + roofOverhang, roofCol)

  // Thick thatch ridge cap (cylinder approximation: flattened box along ridge)
  const ridgeC = pos.clone().addScaledVector(up, foundH + wallH + roofRise)
  addBox(g, ridgeC, up, right, fwd, 0.15, 0.12, hd + roofOverhang * 0.5, jitterColor(roofCol, 0.05, seed + 50))

  // ── Windows with sills and shutters ──
  const numWin = 2 + (rng() > 0.5 ? 1 : 0)
  for (let i = 0; i < numWin; i++) {
    const t = -1 + (2 * (i + 0.5)) / numWin
    const wz = t * (hd - 1.5)
    for (const side of [-1, 1]) {
      // Window recess (dark void)
      const wc = pos.clone()
        .addScaledVector(up, foundH + 1.0)
        .addScaledVector(right, side * (hw + 0.03))
        .addScaledVector(fwd, wz)
      addBox(g, wc, up, right, fwd, 0.05, 0.25, 0.2, darkCol)

      // Window sill (protruding stone ledge)
      const sillC = pos.clone()
        .addScaledVector(up, foundH + 0.72)
        .addScaledVector(right, side * (hw + 0.06))
        .addScaledVector(fwd, wz)
      addBox(g, sillC, up, right, fwd, 0.06, 0.025, 0.24, sillCol)

      // Lintel (beam above window)
      const lintelC = pos.clone()
        .addScaledVector(up, foundH + 1.28)
        .addScaledVector(right, side * (hw + 0.03))
        .addScaledVector(fwd, wz)
      addBox(g, lintelC, up, right, fwd, 0.06, 0.04, 0.26, trimCol)

      // Shutter (one open, angled ~30deg outward) on ~60% of windows
      if (rng() < 0.6) {
        const shutterC = pos.clone()
          .addScaledVector(up, foundH + 1.0)
          .addScaledVector(right, side * (hw + 0.12))
          .addScaledVector(fwd, wz + 0.22)
        addBox(g, shutterC, up, right, fwd, 0.04, 0.24, 0.015, trimCol)
      }
    }
  }

  // ── Door with frame ──
  // Door recess
  const doorC = pos.clone()
    .addScaledVector(up, foundH + 0.8)
    .addScaledVector(right, hw + 0.04)
  addBox(g, doorC, up, right, fwd, 0.06, 0.8, 0.4, darkCol)

  // Door planks (slightly inset from recess)
  const doorPlankC = pos.clone()
    .addScaledVector(up, foundH + 0.8)
    .addScaledVector(right, hw + 0.01)
  addBox(g, doorPlankC, up, right, fwd, 0.02, 0.78, 0.38, doorCol, 0.06, seed + 30)

  // Door lintel
  const doorLintelC = pos.clone()
    .addScaledVector(up, foundH + 1.62)
    .addScaledVector(right, hw + 0.04)
  addBox(g, doorLintelC, up, right, fwd, 0.06, 0.04, 0.46, trimCol)

  // Door threshold (stone step)
  const threshC = pos.clone()
    .addScaledVector(up, foundH + 0.02)
    .addScaledVector(right, hw + 0.1)
  addBox(g, threshC, up, right, fwd, 0.12, 0.04, 0.5, stoneCol)

  // ── Chimney (60% chance — later medieval) ──
  if (rng() < 0.6) {
    const chimH = 1.2
    const chimOffset = (rng() > 0.5 ? 1 : -1) * hd * 0.35
    const chimC = pos.clone()
      .addScaledVector(up, foundH + wallH + roofRise * 0.5 + chimH / 2)
      .addScaledVector(fwd, chimOffset)
    addBox(g, chimC, up, right, fwd, 0.3, chimH / 2, 0.3, stoneCol, 0.06, seed + 40)

    // Chimney cap (slightly wider)
    const capC = chimC.clone().addScaledVector(up, chimH / 2 + 0.06)
    addBox(g, capC, up, right, fwd, 0.35, 0.06, 0.35, stoneCol)
  }
}

function buildChurch(g: GeoArrays, village: VillagePlacement, structure: Structure) {
  const info = STRUCTURE_INFO.church
  const seed = structure.localX * 193 + structure.localZ * 307
  const { position: pos, upVec: up, rightVec: right, forwardVec: fwd } =
    buildingBasis(village, structure, seed)

  const hw = info.width / 2
  const hd = info.depth / 2
  const wallH = info.wallHeight
  const foundH = 0.35
  const roofPitch = info.roofPitch * DEG2RAD
  const roofRise = Math.tan(roofPitch) * hw
  const roofOverhang = 0.25

  const wallCol = varyColor(hexToRGB(info.wallColor), seed, 0.05)
  const wallColDark = weatherDarken(wallCol, 0.3, 0.12)
  const roofCol = varyColor(hexToRGB(info.roofColor), seed + 1, 0.04)
  const stoneCol = varyColor([0.50, 0.48, 0.44] as [number, number, number], seed + 3, 0.05)
  const darkCol: [number, number, number] = [0.12, 0.10, 0.08]
  const glassCol: [number, number, number] = [0.15, 0.12, 0.28]

  // ── Foundation plinth ── (ground AO)
  const foundCenter = pos.clone().addScaledVector(up, foundH / 2)
  addBox(g, foundCenter, up, right, fwd, hw + 0.25, foundH / 2, hd + 0.25, aoColor(stoneCol, 0.6), 0.08, seed)

  // ── Nave walls with weathering ──
  const wallLowerH = wallH * 0.35
  const wallUpperH = wallH * 0.65
  const wallLowerC = pos.clone().addScaledVector(up, foundH + wallLowerH / 2)
  addBox(g, wallLowerC, up, right, fwd, hw, wallLowerH / 2, hd, wallColDark, 0.04, seed + 1)
  const wallUpperC = pos.clone().addScaledVector(up, foundH + wallLowerH + wallUpperH / 2)
  addBox(g, wallUpperC, up, right, fwd, hw, wallUpperH / 2, hd, wallCol, 0.03, seed + 2)

  // ── String course (horizontal stone band at mid-wall) ──
  for (const side of [-1, 1]) {
    const scC = pos.clone()
      .addScaledVector(up, foundH + wallH * 0.45)
      .addScaledVector(right, side * (hw + 0.04))
    addBox(g, scC, up, right, fwd, 0.06, 0.06, hd + 0.1, stoneCol)
  }

  // ── Nave roof ──
  const roofBase = pos.clone().addScaledVector(up, foundH + wallH)
  addRoofWedge(g, roofBase, up, right, fwd, hw + roofOverhang, roofRise, hd + roofOverhang, roofCol)
  // Ridge cap
  const ridgeC = pos.clone().addScaledVector(up, foundH + wallH + roofRise)
  addBox(g, ridgeC, up, right, fwd, 0.12, 0.10, hd + roofOverhang * 0.5, jitterColor(roofCol, 0.04, seed + 50))

  // ── Buttresses along nave (stepped) ──
  const numButtresses = 5
  for (let i = 0; i < numButtresses; i++) {
    const t = -1 + (2 * (i + 0.5)) / numButtresses
    const bz = t * (hd - 1.0)
    for (const side of [-1, 1]) {
      // Lower step (wider)
      const bc1 = pos.clone()
        .addScaledVector(up, foundH + wallH * 0.22)
        .addScaledVector(right, side * (hw + 0.35))
        .addScaledVector(fwd, bz)
      addBox(g, bc1, up, right, fwd, 0.35, wallH * 0.22, 0.20, stoneCol, 0.06, seed + i * 3)
      // Upper step (narrower, taller)
      const bc2 = pos.clone()
        .addScaledVector(up, foundH + wallH * 0.55)
        .addScaledVector(right, side * (hw + 0.22))
        .addScaledVector(fwd, bz)
      addBox(g, bc2, up, right, fwd, 0.22, wallH * 0.33, 0.15, stoneCol, 0.05, seed + i * 3 + 1)
      // Cap slant (small box angled in)
      const bc3 = pos.clone()
        .addScaledVector(up, foundH + wallH * 0.88)
        .addScaledVector(right, side * (hw + 0.15))
        .addScaledVector(fwd, bz)
      addBox(g, bc3, up, right, fwd, 0.12, 0.06, 0.14, stoneCol)
    }
  }

  // ── Lancet windows between buttresses ──
  for (let i = 0; i < numButtresses - 1; i++) {
    const t = -1 + (2 * (i + 1.0)) / numButtresses
    const wz = t * (hd - 1.0)
    for (const side of [-1, 1]) {
      // Dark recess (pointed arch approximated by tall narrow box)
      const wc = pos.clone()
        .addScaledVector(up, foundH + wallH * 0.55)
        .addScaledVector(right, side * (hw + 0.03))
        .addScaledVector(fwd, wz)
      addBox(g, wc, up, right, fwd, 0.05, wallH * 0.30, 0.18, darkCol)
      // Stained glass (inset colored panel)
      const gc = pos.clone()
        .addScaledVector(up, foundH + wallH * 0.55)
        .addScaledVector(right, side * (hw + 0.01))
        .addScaledVector(fwd, wz)
      addBox(g, gc, up, right, fwd, 0.02, wallH * 0.26, 0.14, glassCol)
      // Stone sill
      const sc = pos.clone()
        .addScaledVector(up, foundH + wallH * 0.23)
        .addScaledVector(right, side * (hw + 0.06))
        .addScaledVector(fwd, wz)
      addBox(g, sc, up, right, fwd, 0.06, 0.03, 0.22, stoneCol)
      // Stone hood mould (arch surround)
      const hm = pos.clone()
        .addScaledVector(up, foundH + wallH * 0.87)
        .addScaledVector(right, side * (hw + 0.05))
        .addScaledVector(fwd, wz)
      addBox(g, hm, up, right, fwd, 0.06, 0.04, 0.22, stoneCol)
    }
  }

  // ── West tower ──
  const towerW = 4.5 / 2
  const towerH = 16
  const towerBase = pos.clone().addScaledVector(fwd, -(hd + towerW * 0.2))
  const towerCenter = towerBase.clone().addScaledVector(up, towerH / 2)

  // Tower walls with weathering
  const tLowerH = towerH * 0.35
  const tUpperH = towerH * 0.65
  const tLowerC = towerBase.clone().addScaledVector(up, tLowerH / 2)
  addBox(g, tLowerC, up, right, fwd, towerW, tLowerH / 2, towerW, wallColDark, 0.04, seed + 20)
  const tUpperC = towerBase.clone().addScaledVector(up, tLowerH + tUpperH / 2)
  addBox(g, tUpperC, up, right, fwd, towerW, tUpperH / 2, towerW, wallCol, 0.03, seed + 21)

  // Tower corner buttresses (4 small pilasters)
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const tbC = towerBase.clone()
        .addScaledVector(up, towerH * 0.35)
        .addScaledVector(right, sx * (towerW + 0.1))
        .addScaledVector(fwd, sz * (towerW + 0.1))
      addBox(g, tbC, up, right, fwd, 0.15, towerH * 0.35, 0.15, stoneCol, 0.05, seed + 25)
    }
  }

  // Crenellations on tower (alternating merlons)
  const crenH = 1.2
  const numCrens = 4
  for (const axis of [right, fwd]) {
    const other = axis === right ? fwd : right
    for (let i = 0; i < numCrens; i++) {
      const t = -towerW + (i + 0.5) * (towerW * 2) / numCrens
      for (const side of [-1, 1]) {
        const cc = towerCenter.clone()
          .addScaledVector(up, towerH / 2 + crenH / 2)
          .addScaledVector(axis, t)
          .addScaledVector(other, side * towerW)
        addBox(g, cc, up, right, fwd, 0.35, crenH / 2, 0.35, wallCol, 0.04, seed + i * 7)
      }
    }
  }

  // Tower belfry windows (one per face, tall and narrow)
  const belfryH = towerH * 0.75
  for (const axis of [right, fwd]) {
    for (const side of [-1, 1]) {
      const bwC = towerBase.clone()
        .addScaledVector(up, belfryH)
        .addScaledVector(axis, side * (towerW + 0.03))
      addBox(g, bwC, up, axis, axis === right ? fwd : right, 0.04, 1.0, 0.35, darkCol)
    }
  }

  // ── West door (tower entrance) ──
  const westDoorC = towerBase.clone()
    .addScaledVector(up, foundH + 1.0)
    .addScaledVector(fwd, -(towerW + 0.04))
  addBox(g, westDoorC, up, right, fwd, 0.6, 1.0, 0.05, darkCol)
  // Stone door surround
  for (const sx of [-1, 1]) {
    const jamb = towerBase.clone()
      .addScaledVector(up, foundH + 1.0)
      .addScaledVector(fwd, -(towerW + 0.06))
      .addScaledVector(right, sx * 0.65)
    addBox(g, jamb, up, right, fwd, 0.08, 1.0, 0.06, stoneCol)
  }
  // Arch over door
  const archC = towerBase.clone()
    .addScaledVector(up, foundH + 2.05)
    .addScaledVector(fwd, -(towerW + 0.06))
  addBox(g, archC, up, right, fwd, 0.75, 0.08, 0.06, stoneCol)

  // ── South porch ──
  const porchW = 1.5
  const porchD = 1.8
  const porchH = 3.0
  const porchCenter = pos.clone()
    .addScaledVector(up, porchH / 2)
    .addScaledVector(right, hw + porchD / 2)
    .addScaledVector(fwd, -hd * 0.2)
  addBox(g, porchCenter, up, right, fwd, porchD / 2, porchH / 2, porchW / 2, wallCol, 0.04, seed + 30)
  // Porch door
  const porchDoorC = porchCenter.clone()
    .addScaledVector(right, porchD / 2 + 0.03)
  addBox(g, porchDoorC, up, right, fwd, 0.04, porchH * 0.4, 0.45, darkCol)
  // Porch roof
  const porchRoofBase = porchCenter.clone().addScaledVector(up, porchH / 2)
  addRoofWedge(g, porchRoofBase, up, fwd, right, porchW / 2 + 0.15, porchW * 0.6, porchD / 2 + 0.15, roofCol)

  // ── East window (large, on altar end) ──
  const ewC = pos.clone()
    .addScaledVector(up, foundH + wallH * 0.55)
    .addScaledVector(fwd, hd + 0.03)
  addBox(g, ewC, up, right, fwd, 0.04, wallH * 0.35, 0.5, darkCol)
  addBox(g, ewC.clone().addScaledVector(fwd, -0.02), up, right, fwd, 0.02, wallH * 0.30, 0.42, glassCol)
  // Stone tracery mullion (vertical divider)
  addBox(g, ewC, up, right, fwd, 0.05, wallH * 0.30, 0.03, stoneCol)

  // ── Cross on tower peak ──
  const crossBase = towerCenter.clone().addScaledVector(up, towerH / 2 + crenH + 0.5)
  addBox(g, crossBase, up, right, fwd, 0.06, 0.5, 0.06, stoneCol) // vertical
  const crossArm = crossBase.clone().addScaledVector(up, 0.25)
  addBox(g, crossArm, up, right, fwd, 0.35, 0.06, 0.06, stoneCol) // horizontal
}

function buildCastle(g: GeoArrays, village: VillagePlacement, structure: Structure) {
  const info = STRUCTURE_INFO.castle
  const seed = structure.localX * 211 + structure.localZ * 337
  const { position: pos, upVec: up, rightVec: right, forwardVec: fwd } =
    buildingBasis(village, structure, seed)

  const wallCol = varyColor(hexToRGB(info.wallColor), seed, 0.04)
  const wallColDark = weatherDarken(wallCol, 0.25, 0.15)
  const trimCol = varyColor(hexToRGB(info.trimColor), seed + 1, 0.04)
  const roofCol = varyColor(hexToRGB(info.roofColor), seed + 2, 0.04)
  const stoneCol = varyColor([0.50, 0.48, 0.44] as [number, number, number], seed + 3, 0.04)
  const darkCol: [number, number, number] = [0.10, 0.08, 0.06]

  // ── Central keep: 12x12x20 ──
  const keepW = 6, keepD = 6, keepH = 20

  // Keep walls with weathering (lower half darker)
  const keepLowerH = keepH * 0.35
  const keepUpperH = keepH * 0.65
  const keepLowerC = pos.clone().addScaledVector(up, keepLowerH / 2)
  addBox(g, keepLowerC, up, right, fwd, keepW, keepLowerH / 2, keepD, wallColDark, 0.04, seed)
  const keepUpperC = pos.clone().addScaledVector(up, keepLowerH + keepUpperH / 2)
  addBox(g, keepUpperC, up, right, fwd, keepW, keepUpperH / 2, keepD, wallCol, 0.03, seed + 1)

  // Keep plinth (wider base, ground AO)
  const plinthC = pos.clone().addScaledVector(up, 0.4)
  addBox(g, plinthC, up, right, fwd, keepW + 0.5, 0.4, keepD + 0.5, aoColor(stoneCol, 0.55), 0.06, seed + 2)

  // Keep corner pilasters (vertical stone strips)
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const pcC = pos.clone()
        .addScaledVector(up, keepH / 2)
        .addScaledVector(right, sx * keepW)
        .addScaledVector(fwd, sz * keepD)
      addBox(g, pcC, up, right, fwd, 0.3, keepH / 2, 0.3, stoneCol, 0.05, seed + 3)
    }
  }

  // Crenellations on keep (merlons with arrow slits)
  const crenH = 1.4
  for (const axis of [right, fwd]) {
    const other = axis === right ? fwd : right
    const span = axis === right ? keepW : keepD
    const otherSpan = axis === right ? keepD : keepW
    const numCrens = 5
    for (let i = 0; i < numCrens; i++) {
      const t = -span + (i + 0.5) * (span * 2) / numCrens
      for (const side of [-1, 1]) {
        const cc = pos.clone()
          .addScaledVector(up, keepH + crenH / 2)
          .addScaledVector(axis, t)
          .addScaledVector(other, side * otherSpan)
        addBox(g, cc, up, right, fwd, 0.45, crenH / 2, 0.45, wallCol, 0.04, seed + i * 5)
        // Arrow slit in merlon
        const slitC = cc.clone().addScaledVector(other, side * 0.01)
        addBox(g, slitC, up, axis, other, 0.08, crenH * 0.3, 0.02, darkCol)
      }
    }
  }

  // Keep windows (narrow arrow slits on upper half)
  for (const axis of [right, fwd]) {
    const other = axis === right ? fwd : right
    const span = axis === right ? keepW : keepD
    for (const side of [-1, 1]) {
      for (let row = 0; row < 2; row++) {
        const winY = keepH * 0.5 + row * keepH * 0.2
        for (let i = 0; i < 2; i++) {
          const t = (i === 0 ? -0.4 : 0.4) * span
          const wc = pos.clone()
            .addScaledVector(up, winY)
            .addScaledVector(axis, t)
            .addScaledVector(other, side * (span + 0.03))
          addBox(g, wc, up, axis, other, 0.12, 0.6, 0.04, darkCol)
        }
      }
    }
  }

  // ── 4 corner towers ──
  const towerR = 3.5, towerH = 22
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const tBase = pos.clone()
        .addScaledVector(right, sx * (keepW + towerR * 0.8))
        .addScaledVector(fwd, sz * (keepD + towerR * 0.8))
      const tc = tBase.clone().addScaledVector(up, towerH / 2)

      // Tower body (lower darker, upper lighter)
      const tLowerC = tBase.clone().addScaledVector(up, towerH * 0.35 / 2)
      addPrism(g, tLowerC, up, right, fwd, towerR, towerR, towerH * 0.35 / 2, 10, trimCol)
      const tUpperC = tBase.clone().addScaledVector(up, towerH * 0.35 + towerH * 0.65 / 2)
      addPrism(g, tUpperC, up, right, fwd, towerR, towerR, towerH * 0.65 / 2, 10,
        jitterColor(trimCol, 0.04, seed + sx * 10 + sz * 20))

      // Conical top with overhanging eave
      const coneC = tc.clone().addScaledVector(up, towerH / 2 + 2.5)
      addCone(g, coneC, up, right, fwd, towerR + 0.5, 2.5, 10, roofCol)

      // Tower arrow slits (4 per tower, at cardinal directions)
      for (let a = 0; a < 4; a++) {
        const angle = (a / 4) * Math.PI * 2
        const slitDir = right.clone().multiplyScalar(Math.cos(angle))
          .add(fwd.clone().multiplyScalar(Math.sin(angle)))
        const slitPerp = right.clone().multiplyScalar(-Math.sin(angle))
          .add(fwd.clone().multiplyScalar(Math.cos(angle)))
        for (const slitH of [towerH * 0.4, towerH * 0.65]) {
          const slC = tBase.clone()
            .addScaledVector(up, slitH)
            .addScaledVector(slitDir, towerR + 0.03)
          addBox(g, slC, up, slitPerp, slitDir, 0.10, 0.5, 0.04, darkCol)
        }
      }
    }
  }

  // ── Curtain walls ──
  const curtainH = 10
  const curtainT = 1.0
  const wallDist = keepW + towerR * 0.8
  for (const side of [-1, 1]) {
    // Along right axis
    const wc1 = pos.clone()
      .addScaledVector(up, curtainH / 2)
      .addScaledVector(fwd, side * (keepD + towerR * 0.8))
    addBox(g, wc1, up, right, fwd, wallDist, curtainH / 2, curtainT, trimCol, 0.03, seed + 10)
    // Along forward axis
    const wc2 = pos.clone()
      .addScaledVector(up, curtainH / 2)
      .addScaledVector(right, side * (keepW + towerR * 0.8))
    addBox(g, wc2, up, right, fwd, curtainT, curtainH / 2, wallDist, trimCol, 0.03, seed + 11)

    // Wall walk crenellations on curtain walls
    const cwCrenH = 1.0
    const numCwCrens = 6
    // Right-axis walls
    for (let i = 0; i < numCwCrens; i++) {
      const t = -wallDist + (i + 0.5) * (wallDist * 2) / numCwCrens
      const cwC = pos.clone()
        .addScaledVector(up, curtainH + cwCrenH / 2)
        .addScaledVector(right, t)
        .addScaledVector(fwd, side * (keepD + towerR * 0.8))
      addBox(g, cwC, up, right, fwd, 0.35, cwCrenH / 2, curtainT + 0.1, trimCol, 0.04, seed + i)
    }
    // Forward-axis walls
    for (let i = 0; i < numCwCrens; i++) {
      const t = -wallDist + (i + 0.5) * (wallDist * 2) / numCwCrens
      const cwC = pos.clone()
        .addScaledVector(up, curtainH + cwCrenH / 2)
        .addScaledVector(fwd, t)
        .addScaledVector(right, side * (keepW + towerR * 0.8))
      addBox(g, cwC, up, right, fwd, curtainT + 0.1, cwCrenH / 2, 0.35, trimCol, 0.04, seed + i + 20)
    }
  }

  // ── Gatehouse ──
  const ghY = curtainH * 0.7
  const ghZ = keepD + towerR * 0.8
  const ghCenter = pos.clone()
    .addScaledVector(up, ghY)
    .addScaledVector(fwd, ghZ)
  addBox(g, ghCenter, up, right, fwd, 2.5, ghY, curtainT + 0.8, trimCol, 0.04, seed + 15)

  // Gatehouse flanking towers
  for (const sx of [-1, 1]) {
    const ghTower = pos.clone()
      .addScaledVector(up, ghY)
      .addScaledVector(right, sx * 3.0)
      .addScaledVector(fwd, ghZ)
    addPrism(g, ghTower, up, right, fwd, 1.5, 1.5, ghY, 8, trimCol)
    // Small conical roof on gatehouse towers
    const ghCone = ghTower.clone().addScaledVector(up, ghY + 1.0)
    addCone(g, ghCone, up, right, fwd, 1.7, 1.0, 8, roofCol)
  }

  // Gate arch (dark opening)
  const gateC = pos.clone()
    .addScaledVector(up, curtainH * 0.35)
    .addScaledVector(fwd, ghZ + curtainT + 0.85)
  addBox(g, gateC, up, right, fwd, 1.5, curtainH * 0.35, 0.06, darkCol)

  // Portcullis groove lines (vertical dark strips flanking gate)
  for (const sx of [-1, 1]) {
    const pgC = gateC.clone().addScaledVector(right, sx * 1.55)
    addBox(g, pgC, up, right, fwd, 0.08, curtainH * 0.35, 0.08, darkCol)
  }
}

function buildPub(g: GeoArrays, village: VillagePlacement, structure: Structure) {
  const info = STRUCTURE_INFO.pub
  const seed = structure.localX * 163 + structure.localZ * 271
  const { position: pos, upVec: up, rightVec: right, forwardVec: fwd, rng } =
    buildingBasis(village, structure, seed)

  const hw = info.width / 2
  const hd = info.depth / 2
  const wallH = info.wallHeight
  const foundH = 0.3
  const roofPitch = info.roofPitch * DEG2RAD
  const roofRise = Math.tan(roofPitch) * hw
  const roofOverhang = 0.35
  const jetty = 0.35 // upper floor overhang
  const lowerH = wallH * 0.5
  const upperH = wallH * 0.5

  const wallCol = varyColor(hexToRGB(info.wallColor), seed, 0.06)
  const wallColDark = weatherDarken(wallCol, 0.3, 0.15)
  const roofCol = varyColor(hexToRGB(info.roofColor), seed + 1, 0.05)
  const trimCol = varyColor(hexToRGB(info.trimColor), seed + 2, 0.04)
  const stoneCol = varyColor([0.50, 0.48, 0.44] as [number, number, number], seed + 3, 0.05)
  const darkCol: [number, number, number] = [0.10, 0.08, 0.06]
  const doorCol: [number, number, number] = [0.42, 0.29, 0.16]
  const sillCol: [number, number, number] = [0.50, 0.48, 0.44]

  // ── Foundation ── (ground AO)
  const foundCenter = pos.clone().addScaledVector(up, foundH / 2)
  addBox(g, foundCenter, up, right, fwd, hw + 0.18, foundH / 2, hd + 0.18, aoColor(stoneCol, 0.6), 0.07, seed)

  // ── Lower storey walls with weathering ──
  const lowerDarkH = lowerH * 0.4
  const lowerLightH = lowerH * 0.6
  const lowerDarkC = pos.clone().addScaledVector(up, foundH + lowerDarkH / 2)
  addBox(g, lowerDarkC, up, right, fwd, hw, lowerDarkH / 2, hd, wallColDark, 0.05, seed + 1)
  const lowerLightC = pos.clone().addScaledVector(up, foundH + lowerDarkH + lowerLightH / 2)
  addBox(g, lowerLightC, up, right, fwd, hw, lowerLightH / 2, hd, wallCol, 0.04, seed + 2)

  // ── Jetty beam (bressummer) — the overhanging floor plate ──
  const jettyBeamC = pos.clone().addScaledVector(up, foundH + lowerH)
  addBox(g, jettyBeamC, up, right, fwd, hw + jetty + 0.03, 0.08, hd + jetty + 0.03, trimCol)

  // ── Jetty brackets (decorative corbels under overhang) ──
  const numBrackets = 5
  for (let i = 0; i < numBrackets; i++) {
    const t = -1 + (2 * (i + 0.5)) / numBrackets
    const bz = t * (hd - 0.8)
    for (const side of [-1, 1]) {
      const bc = pos.clone()
        .addScaledVector(up, foundH + lowerH - 0.12)
        .addScaledVector(right, side * (hw + jetty * 0.4))
        .addScaledVector(fwd, bz)
      addBox(g, bc, up, right, fwd, jetty * 0.35, 0.10, 0.06, trimCol)
    }
  }

  // ── Jettied upper floor ──
  const upperCenter = pos.clone().addScaledVector(up, foundH + lowerH + upperH / 2)
  addBox(g, upperCenter, up, right, fwd, hw + jetty, upperH / 2, hd + jetty, wallCol, 0.04, seed + 3)

  // ── Half-timber framing on upper floor ──
  // Vertical studs
  const numStuds = 5
  for (let i = 0; i < numStuds; i++) {
    const t = -1 + (2 * (i + 0.5)) / numStuds
    const tz = t * (hd + jetty - 0.5)
    for (const side of [-1, 1]) {
      const tc = upperCenter.clone()
        .addScaledVector(right, side * (hw + jetty + 0.02))
        .addScaledVector(fwd, tz)
      addBox(g, tc, up, right, fwd, 0.05, upperH / 2, 0.06, trimCol)
    }
  }
  // Horizontal mid-rail on upper
  for (const side of [-1, 1]) {
    const mrC = upperCenter.clone()
      .addScaledVector(right, side * (hw + jetty + 0.02))
    addBox(g, mrC, up, right, fwd, 0.05, 0.05, hd + jetty, trimCol)
  }
  // Corner posts
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const cpC = upperCenter.clone()
        .addScaledVector(right, sx * (hw + jetty))
        .addScaledVector(fwd, sz * (hd + jetty))
      addBox(g, cpC, up, right, fwd, 0.08, upperH / 2, 0.08, trimCol)
    }
  }
  // Diagonal braces on upper corners
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const dbC = upperCenter.clone()
        .addScaledVector(up, -upperH * 0.15)
        .addScaledVector(right, sx * (hw + jetty + 0.02))
        .addScaledVector(fwd, sz * (hd + jetty - 0.5))
      addBox(g, dbC, up, right, fwd, 0.04, upperH * 0.2, 0.3, trimCol)
    }
  }
  // Eaves beam
  for (const side of [-1, 1]) {
    const ebC = pos.clone()
      .addScaledVector(up, foundH + wallH)
      .addScaledVector(right, side * (hw + jetty + 0.02))
    addBox(g, ebC, up, right, fwd, 0.06, 0.07, hd + jetty + 0.03, trimCol)
  }

  // ── Exposed rafter tails ──
  const rafterSpacing = 0.9
  const numRafters = Math.floor((info.depth + jetty * 2) / rafterSpacing)
  for (let i = 0; i < numRafters; i++) {
    const rz = -(hd + jetty) + (i + 0.5) * rafterSpacing
    for (const side of [-1, 1]) {
      const rc = pos.clone()
        .addScaledVector(up, foundH + wallH - 0.04)
        .addScaledVector(right, side * (hw + jetty + roofOverhang * 0.5))
        .addScaledVector(fwd, rz)
      addBox(g, rc, up, right, fwd, roofOverhang * 0.35, 0.035, 0.03, trimCol)
    }
  }

  // ── Roof with ridge cap ──
  const roofBase = pos.clone().addScaledVector(up, foundH + wallH)
  addRoofWedge(g, roofBase, up, right, fwd, hw + jetty + roofOverhang, roofRise, hd + jetty + roofOverhang, roofCol)
  const ridgeC = pos.clone().addScaledVector(up, foundH + wallH + roofRise)
  addBox(g, ridgeC, up, right, fwd, 0.14, 0.11, hd + jetty + roofOverhang * 0.5, jitterColor(roofCol, 0.05, seed + 50))

  // ── Windows with sills, lintels, shutters ──
  const numWin = 3 + (rng() > 0.5 ? 1 : 0)
  for (let i = 0; i < numWin; i++) {
    const t = -1 + (2 * (i + 0.5)) / numWin
    const wz = t * (hd - 1.2)
    for (const side of [-1, 1]) {
      // Lower floor windows
      const wlC = pos.clone()
        .addScaledVector(up, foundH + lowerH * 0.5)
        .addScaledVector(right, side * (hw + 0.03))
        .addScaledVector(fwd, wz)
      addBox(g, wlC, up, right, fwd, 0.05, 0.28, 0.22, darkCol)
      // Lower sill
      const wlSill = pos.clone()
        .addScaledVector(up, foundH + lowerH * 0.2)
        .addScaledVector(right, side * (hw + 0.06))
        .addScaledVector(fwd, wz)
      addBox(g, wlSill, up, right, fwd, 0.06, 0.025, 0.26, sillCol)
      // Lower lintel
      const wlLint = pos.clone()
        .addScaledVector(up, foundH + lowerH * 0.8)
        .addScaledVector(right, side * (hw + 0.03))
        .addScaledVector(fwd, wz)
      addBox(g, wlLint, up, right, fwd, 0.06, 0.04, 0.28, trimCol)

      // Upper floor windows (wider due to jetty, with shutters)
      const uwz = wz
      const uwC = pos.clone()
        .addScaledVector(up, foundH + lowerH + upperH * 0.5)
        .addScaledVector(right, side * (hw + jetty + 0.03))
        .addScaledVector(fwd, uwz)
      addBox(g, uwC, up, right, fwd, 0.05, 0.28, 0.22, darkCol)
      // Upper sill
      const uwSill = uwC.clone().addScaledVector(up, -0.3).addScaledVector(right, side * 0.03)
      addBox(g, uwSill, up, right, fwd, 0.06, 0.025, 0.26, sillCol)
      // Upper lintel
      const uwLint = uwC.clone().addScaledVector(up, 0.3)
      addBox(g, uwLint, up, right, fwd, 0.06, 0.04, 0.28, trimCol)
      // Shutter (one open)
      if (rng() < 0.5) {
        const shutC = uwC.clone()
          .addScaledVector(right, side * 0.08)
          .addScaledVector(fwd, 0.24)
        addBox(g, shutC, up, right, fwd, 0.04, 0.26, 0.015, trimCol)
      }
    }
  }

  // ── Door with frame and threshold ──
  const doorC = pos.clone()
    .addScaledVector(up, foundH + 0.85)
    .addScaledVector(fwd, hd + 0.04)
  addBox(g, doorC, up, right, fwd, 0.45, 0.85, 0.05, darkCol)
  // Door planks
  const dpC = doorC.clone().addScaledVector(fwd, -0.02)
  addBox(g, dpC, up, right, fwd, 0.43, 0.83, 0.02, doorCol, 0.06, seed + 30)
  // Door lintel
  const dlC = doorC.clone().addScaledVector(up, 0.88)
  addBox(g, dlC, up, right, fwd, 0.55, 0.05, 0.06, trimCol)
  // Threshold step
  const dtC = pos.clone()
    .addScaledVector(up, foundH + 0.03)
    .addScaledVector(fwd, hd + 0.12)
  addBox(g, dtC, up, right, fwd, 0.55, 0.04, 0.14, stoneCol)

  // ── Chimney ──
  const chimH = 1.5
  const chimC = pos.clone()
    .addScaledVector(up, foundH + wallH + roofRise * 0.4 + chimH / 2)
    .addScaledVector(fwd, -hd * 0.4)
  addBox(g, chimC, up, right, fwd, 0.35, chimH / 2, 0.35, stoneCol, 0.06, seed + 40)
  const chimCap = chimC.clone().addScaledVector(up, chimH / 2 + 0.06)
  addBox(g, chimCap, up, right, fwd, 0.40, 0.06, 0.40, stoneCol)

  // ── Hanging sign ──
  const signArmC = pos.clone()
    .addScaledVector(up, foundH + lowerH + 0.4)
    .addScaledVector(fwd, hd + jetty + 0.5)
  addBox(g, signArmC, up, right, fwd, 0.04, 0.04, 0.5, trimCol)
  const signC = signArmC.clone().addScaledVector(fwd, 0.5).addScaledVector(up, -0.35)
  addBox(g, signC, up, right, fwd, 0.35, 0.28, 0.04, jitterColor(trimCol, 0.08, seed + 55))
  // Sign bracket (small L-shape)
  const bracketC = signArmC.clone().addScaledVector(fwd, -0.1).addScaledVector(up, -0.12)
  addBox(g, bracketC, up, right, fwd, 0.03, 0.12, 0.03, trimCol)
}

function buildStable(g: GeoArrays, village: VillagePlacement, structure: Structure) {
  const info = STRUCTURE_INFO.stable
  const seed = structure.localX * 181 + structure.localZ * 293
  const { position: pos, upVec: up, rightVec: right, forwardVec: fwd, rng } =
    buildingBasis(village, structure, seed)

  const hw = info.width / 2
  const hd = info.depth / 2
  const wallH = info.wallHeight
  const foundH = 0.25
  const roofPitch = info.roofPitch * DEG2RAD
  const roofRise = Math.tan(roofPitch) * hw
  const roofOverhang = 0.35

  const wallCol = varyColor(hexToRGB(info.wallColor), seed, 0.06)
  const wallColDark = weatherDarken(wallCol, 0.3, 0.18)
  const roofCol = varyColor(hexToRGB(info.roofColor), seed + 1, 0.05)
  const trimCol = varyColor(hexToRGB(info.trimColor), seed + 2, 0.04)
  const stoneCol = varyColor([0.50, 0.48, 0.44] as [number, number, number], seed + 3, 0.05)
  const darkCol: [number, number, number] = [0.10, 0.08, 0.06]
  const doorCol: [number, number, number] = [0.38, 0.25, 0.12]

  // ── Foundation ── (ground AO)
  const foundCenter = pos.clone().addScaledVector(up, foundH / 2)
  addBox(g, foundCenter, up, right, fwd, hw + 0.12, foundH / 2, hd + 0.12, aoColor(stoneCol, 0.6), 0.07, seed)

  // ── Walls with weathering ──
  const wallLowerH = wallH * 0.4
  const wallUpperH = wallH * 0.6
  const wallLowerC = pos.clone().addScaledVector(up, foundH + wallLowerH / 2)
  addBox(g, wallLowerC, up, right, fwd, hw, wallLowerH / 2, hd, wallColDark, 0.05, seed + 1)
  const wallUpperC = pos.clone().addScaledVector(up, foundH + wallLowerH + wallUpperH / 2)
  addBox(g, wallUpperC, up, right, fwd, hw, wallUpperH / 2, hd, wallCol, 0.04, seed + 2)

  // ── Horizontal plank lines (board-and-batten style) ──
  const numPlanks = 4
  for (let i = 0; i < numPlanks; i++) {
    const pH = foundH + wallH * (0.2 + i * 0.2)
    for (const side of [-1, 1]) {
      const plC = pos.clone()
        .addScaledVector(up, pH)
        .addScaledVector(right, side * (hw + 0.02))
      addBox(g, plC, up, right, fwd, 0.04, 0.03, hd, trimCol)
    }
  }

  // ── Eaves beam ──
  for (const side of [-1, 1]) {
    const beamC = pos.clone()
      .addScaledVector(up, foundH + wallH)
      .addScaledVector(right, side * (hw + 0.02))
    addBox(g, beamC, up, right, fwd, 0.06, 0.07, hd + 0.03, trimCol)
  }

  // ── Corner posts ──
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const cpC = pos.clone()
        .addScaledVector(up, foundH + wallH / 2)
        .addScaledVector(right, sx * hw)
        .addScaledVector(fwd, sz * hd)
      addBox(g, cpC, up, right, fwd, 0.08, wallH / 2, 0.08, trimCol)
    }
  }

  // ── Exposed rafter tails ──
  const rafterSpacing = 1.0
  const numRafters = Math.floor(info.depth / rafterSpacing)
  for (let i = 0; i < numRafters; i++) {
    const rz = -hd + (i + 0.5) * rafterSpacing
    for (const side of [-1, 1]) {
      const rc = pos.clone()
        .addScaledVector(up, foundH + wallH - 0.04)
        .addScaledVector(right, side * (hw + roofOverhang * 0.5))
        .addScaledVector(fwd, rz)
      addBox(g, rc, up, right, fwd, roofOverhang * 0.35, 0.035, 0.03, trimCol)
    }
  }

  // ── Roof with ridge cap ──
  const roofBase = pos.clone().addScaledVector(up, foundH + wallH)
  addRoofWedge(g, roofBase, up, right, fwd, hw + roofOverhang, roofRise, hd + roofOverhang, roofCol)
  const ridgeC = pos.clone().addScaledVector(up, foundH + wallH + roofRise)
  addBox(g, ridgeC, up, right, fwd, 0.12, 0.10, hd + roofOverhang * 0.5, jitterColor(roofCol, 0.05, seed + 50))

  // ── Large stable door (double door) ──
  const doorH = 1.4
  const doorC = pos.clone()
    .addScaledVector(up, foundH + doorH)
    .addScaledVector(fwd, hd + 0.04)
  addBox(g, doorC, up, right, fwd, 1.1, doorH, 0.05, darkCol)
  // Door planks (two halves with gap)
  for (const sx of [-0.5, 0.5]) {
    const dpC = doorC.clone()
      .addScaledVector(fwd, -0.02)
      .addScaledVector(right, sx)
    addBox(g, dpC, up, right, fwd, 0.48, doorH - 0.02, 0.02, doorCol, 0.06, seed + 30 + sx * 10)
  }
  // Door lintel (heavy timber)
  const dlC = pos.clone()
    .addScaledVector(up, foundH + doorH * 2 + 0.06)
    .addScaledVector(fwd, hd + 0.04)
  addBox(g, dlC, up, right, fwd, 1.2, 0.06, 0.08, trimCol)
  // Stone threshold
  const dtC = pos.clone()
    .addScaledVector(up, foundH + 0.03)
    .addScaledVector(fwd, hd + 0.12)
  addBox(g, dtC, up, right, fwd, 1.2, 0.04, 0.14, stoneCol)

  // ── Ventilation windows (narrow slits with lintels) ──
  const numSlits = 4
  for (let i = 0; i < numSlits; i++) {
    const t = -1 + (2 * (i + 0.5)) / numSlits
    const sz = t * (hd - 1.5)
    for (const side of [-1, 1]) {
      const sc = pos.clone()
        .addScaledVector(up, foundH + wallH * 0.78)
        .addScaledVector(right, side * (hw + 0.03))
        .addScaledVector(fwd, sz)
      addBox(g, sc, up, right, fwd, 0.04, 0.15, 0.3, darkCol)
      // Small lintel
      const slC = sc.clone().addScaledVector(up, 0.18)
      addBox(g, slC, up, right, fwd, 0.05, 0.03, 0.34, trimCol)
    }
  }

  // ── Hay loft door on gable end ──
  const loftDoor = pos.clone()
    .addScaledVector(up, foundH + wallH + roofRise * 0.3)
    .addScaledVector(fwd, -(hd + 0.03))
  addBox(g, loftDoor, up, right, fwd, 0.55, 0.45, 0.04, darkCol)
  // Loft door planks
  const lfpC = loftDoor.clone().addScaledVector(fwd, 0.02)
  addBox(g, lfpC, up, right, fwd, 0.50, 0.40, 0.02, doorCol, 0.06, seed + 35)
  // Loft beam (projecting hoist beam)
  const hoistC = pos.clone()
    .addScaledVector(up, foundH + wallH + roofRise * 0.35)
    .addScaledVector(fwd, -(hd + 0.5))
  addBox(g, hoistC, up, right, fwd, 0.06, 0.06, 0.5, trimCol)

  // ── Stall dividers visible through door (internal hint) ──
  const numStalls = rng() > 0.5 ? 3 : 4
  for (let i = 1; i < numStalls; i++) {
    const stallZ = -hd + 0.5 + i * ((hd * 2 - 1.0) / numStalls)
    const stallC = pos.clone()
      .addScaledVector(up, foundH + wallH * 0.35)
      .addScaledVector(fwd, stallZ)
    addBox(g, stallC, up, fwd, right, 0.03, wallH * 0.35, hw * 0.4, trimCol)
  }
}

function buildArmory(g: GeoArrays, village: VillagePlacement, structure: Structure) {
  // Smithy: open front, prominent chimney, forge area
  const info = STRUCTURE_INFO.armory
  const seed = structure.localX * 199 + structure.localZ * 313
  const { position: pos, upVec: up, rightVec: right, forwardVec: fwd } =
    buildingBasis(village, structure, seed)

  const hw = info.width / 2
  const hd = info.depth / 2
  const wallH = info.wallHeight
  const foundH = 0.25
  const roofPitch = info.roofPitch * DEG2RAD
  const roofRise = Math.tan(roofPitch) * hw
  const roofOverhang = 0.4

  const wallCol = varyColor(hexToRGB(info.wallColor), seed, 0.05)
  const wallColDark = weatherDarken(wallCol, 0.3, 0.2)
  const roofCol = varyColor(hexToRGB(info.roofColor), seed + 1, 0.05)
  const trimCol = varyColor(hexToRGB(info.trimColor), seed + 2, 0.04)
  const stoneCol = varyColor([0.50, 0.48, 0.44] as [number, number, number], seed + 3, 0.05)
  const stoneDark = varyColor([0.35, 0.33, 0.30] as [number, number, number], seed + 4, 0.05)
  const darkCol: [number, number, number] = [0.10, 0.08, 0.06]
  const ironCol: [number, number, number] = [0.25, 0.24, 0.22]

  // ── Foundation (raised stone platform, ground AO) ──
  const foundCenter = pos.clone().addScaledVector(up, foundH / 2)
  addBox(g, foundCenter, up, right, fwd, hw + 0.15, foundH / 2, hd + 0.15, aoColor(stoneCol, 0.6), 0.08, seed)

  // ── Back wall (full height, stone lower / timber upper) ──
  const backLowerH = wallH * 0.5
  const backUpperH = wallH * 0.5
  const backLowerC = pos.clone()
    .addScaledVector(up, foundH + backLowerH / 2)
    .addScaledVector(fwd, -hd + 0.15)
  addBox(g, backLowerC, up, right, fwd, hw, backLowerH / 2, 0.15, stoneDark, 0.06, seed + 1)
  const backUpperC = pos.clone()
    .addScaledVector(up, foundH + backLowerH + backUpperH / 2)
    .addScaledVector(fwd, -hd + 0.15)
  addBox(g, backUpperC, up, right, fwd, hw, backUpperH / 2, 0.15, wallCol, 0.05, seed + 2)

  // ── Side walls (weathered) ──
  for (const side of [-1, 1]) {
    const sLowerC = pos.clone()
      .addScaledVector(up, foundH + wallH * 0.25)
      .addScaledVector(right, side * (hw - 0.15))
    addBox(g, sLowerC, up, right, fwd, 0.15, wallH * 0.25, hd, wallColDark, 0.05, seed + 3)
    const sUpperC = pos.clone()
      .addScaledVector(up, foundH + wallH * 0.5 + wallH * 0.25)
      .addScaledVector(right, side * (hw - 0.15))
    addBox(g, sUpperC, up, right, fwd, 0.15, wallH * 0.25, hd, wallCol, 0.04, seed + 4)
  }

  // ── Support posts at open front ──
  for (const sx of [-1, 1]) {
    const pillarC = pos.clone()
      .addScaledVector(up, foundH + wallH / 2)
      .addScaledVector(right, sx * (hw - 0.5))
      .addScaledVector(fwd, hd - 0.3)
    addBox(g, pillarC, up, right, fwd, 0.18, wallH / 2, 0.18, trimCol)
    // Angled brace from pillar to eaves
    const braceC = pos.clone()
      .addScaledVector(up, foundH + wallH * 0.75)
      .addScaledVector(right, sx * (hw - 0.5))
      .addScaledVector(fwd, hd - 0.8)
    addBox(g, braceC, up, right, fwd, 0.06, wallH * 0.18, 0.4, trimCol)
  }

  // ── Lintel beam across open front ──
  const lintelC = pos.clone()
    .addScaledVector(up, foundH + wallH)
    .addScaledVector(fwd, hd - 0.3)
  addBox(g, lintelC, up, right, fwd, hw, 0.08, 0.08, trimCol)

  // ── Eaves beams ──
  for (const side of [-1, 1]) {
    const ebC = pos.clone()
      .addScaledVector(up, foundH + wallH)
      .addScaledVector(right, side * (hw + 0.02))
    addBox(g, ebC, up, right, fwd, 0.06, 0.07, hd + 0.03, trimCol)
  }

  // ── Roof with overhang ──
  const roofBase = pos.clone().addScaledVector(up, foundH + wallH)
  addRoofWedge(g, roofBase, up, right, fwd, hw + roofOverhang, roofRise, hd + roofOverhang, roofCol)
  // Ridge
  const ridgeC = pos.clone().addScaledVector(up, foundH + wallH + roofRise)
  addBox(g, ridgeC, up, right, fwd, 0.10, 0.08, hd + roofOverhang * 0.5, jitterColor(roofCol, 0.05, seed + 50))

  // ── Prominent stone chimney (larger, with flue) ──
  const chimH = roofRise + 4.5
  const chimC = pos.clone()
    .addScaledVector(up, foundH + chimH / 2)
    .addScaledVector(right, -(hw - 0.3))
    .addScaledVector(fwd, -(hd - 0.3))
  addBox(g, chimC, up, right, fwd, 0.6, chimH / 2, 0.6, stoneDark, 0.06, seed + 40)
  // Chimney cap
  const chimCapC = chimC.clone().addScaledVector(up, chimH / 2 + 0.08)
  addBox(g, chimCapC, up, right, fwd, 0.7, 0.08, 0.7, stoneCol)
  // Chimney breast (wider base inside)
  const breastC = pos.clone()
    .addScaledVector(up, foundH + wallH * 0.35)
    .addScaledVector(right, -(hw - 0.5))
    .addScaledVector(fwd, -(hd - 0.15))
  addBox(g, breastC, up, right, fwd, 0.8, wallH * 0.35, 0.5, stoneDark, 0.05, seed + 41)

  // ── Forge hearth (stone platform at base of chimney) ──
  const forgeC = pos.clone()
    .addScaledVector(up, foundH + 0.25)
    .addScaledVector(right, -(hw - 0.8))
    .addScaledVector(fwd, -(hd - 1.0))
  addBox(g, forgeC, up, right, fwd, 0.8, 0.25, 0.8, stoneDark, 0.08, seed + 42)

  // ── Anvil (small dark box on a stump) ──
  const anvilStumpC = pos.clone()
    .addScaledVector(up, foundH + 0.3)
    .addScaledVector(right, 0.3)
    .addScaledVector(fwd, 0.0)
  addBox(g, anvilStumpC, up, right, fwd, 0.2, 0.3, 0.2, trimCol)
  const anvilC = anvilStumpC.clone().addScaledVector(up, 0.35)
  addBox(g, anvilC, up, right, fwd, 0.25, 0.06, 0.15, ironCol)
  // Anvil horn
  const hornC = anvilC.clone().addScaledVector(fwd, 0.18)
  addBox(g, hornC, up, right, fwd, 0.08, 0.04, 0.08, ironCol)

  // ── Quenching trough (near forge) ──
  const qtC = pos.clone()
    .addScaledVector(up, foundH + 0.2)
    .addScaledVector(right, hw * 0.3)
    .addScaledVector(fwd, -(hd * 0.3))
  addBox(g, qtC, up, right, fwd, 0.6, 0.2, 0.3, trimCol)

  // ── Small window on back wall ──
  const bwC = pos.clone()
    .addScaledVector(up, foundH + wallH * 0.65)
    .addScaledVector(fwd, -(hd + 0.03))
    .addScaledVector(right, hw * 0.3)
  addBox(g, bwC, up, right, fwd, 0.15, 0.18, 0.04, darkCol)
}

function buildBarn(g: GeoArrays, village: VillagePlacement, structure: Structure) {
  const info = STRUCTURE_INFO.barn
  const seed = structure.localX * 223 + structure.localZ * 347
  const { position: pos, upVec: up, rightVec: right, forwardVec: fwd, rng } =
    buildingBasis(village, structure, seed)

  const hw = info.width / 2
  const hd = info.depth / 2
  const wallH = info.wallHeight
  const foundH = 0.3
  const roofPitch = info.roofPitch * DEG2RAD
  const roofRise = Math.tan(roofPitch) * hw
  const roofOverhang = 0.45

  const wallCol = varyColor(hexToRGB(info.wallColor), seed, 0.06)
  const wallColDark = weatherDarken(wallCol, 0.3, 0.18)
  const roofCol = varyColor(hexToRGB(info.roofColor), seed + 1, 0.05)
  const trimCol = varyColor(hexToRGB(info.trimColor), seed + 2, 0.04)
  const stoneCol = varyColor([0.50, 0.48, 0.44] as [number, number, number], seed + 3, 0.05)
  const darkCol: [number, number, number] = [0.10, 0.08, 0.06]
  const doorCol: [number, number, number] = [0.38, 0.25, 0.12]

  // ── Foundation (wide stone plinth, ground AO) ──
  const foundCenter = pos.clone().addScaledVector(up, foundH / 2)
  addBox(g, foundCenter, up, right, fwd, hw + 0.20, foundH / 2, hd + 0.20, aoColor(stoneCol, 0.6), 0.08, seed)

  // ── Walls with weathering ──
  const wallLowerH = wallH * 0.4
  const wallUpperH = wallH * 0.6
  const wallLowerC = pos.clone().addScaledVector(up, foundH + wallLowerH / 2)
  addBox(g, wallLowerC, up, right, fwd, hw, wallLowerH / 2, hd, wallColDark, 0.05, seed + 1)
  const wallUpperC = pos.clone().addScaledVector(up, foundH + wallLowerH + wallUpperH / 2)
  addBox(g, wallUpperC, up, right, fwd, hw, wallUpperH / 2, hd, wallCol, 0.04, seed + 2)

  // ── Heavy timber frame (cruck-frame style) ──
  // Vertical studs on long walls
  const numStuds = 5
  for (let i = 0; i < numStuds; i++) {
    const t = -1 + (2 * (i + 0.5)) / numStuds
    const studZ = t * (hd - 0.5)
    for (const side of [-1, 1]) {
      const tc = pos.clone()
        .addScaledVector(up, foundH + wallH / 2)
        .addScaledVector(right, side * (hw + 0.02))
        .addScaledVector(fwd, studZ)
      addBox(g, tc, up, right, fwd, 0.06, wallH / 2, 0.08, trimCol)
    }
  }

  // Sill beam
  for (const side of [-1, 1]) {
    const sillC = pos.clone()
      .addScaledVector(up, foundH + 0.06)
      .addScaledVector(right, side * (hw + 0.02))
    addBox(g, sillC, up, right, fwd, 0.06, 0.06, hd, trimCol)
  }

  // Eaves beam
  for (const side of [-1, 1]) {
    const beamC = pos.clone()
      .addScaledVector(up, foundH + wallH)
      .addScaledVector(right, side * (hw + 0.02))
    addBox(g, beamC, up, right, fwd, 0.07, 0.08, hd + 0.03, trimCol)
  }

  // Mid-rail
  for (const side of [-1, 1]) {
    const mrC = pos.clone()
      .addScaledVector(up, foundH + wallH * 0.5)
      .addScaledVector(right, side * (hw + 0.02))
    addBox(g, mrC, up, right, fwd, 0.055, 0.05, hd, trimCol)
  }

  // Corner posts (larger)
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const cpC = pos.clone()
        .addScaledVector(up, foundH + wallH / 2)
        .addScaledVector(right, sx * hw)
        .addScaledVector(fwd, sz * hd)
      addBox(g, cpC, up, right, fwd, 0.10, wallH / 2, 0.10, trimCol)
    }
  }

  // Cross-bracing (X-pattern between studs on long walls)
  for (let i = 0; i < numStuds - 1; i++) {
    const t1 = -1 + (2 * (i + 0.5)) / numStuds
    const t2 = -1 + (2 * (i + 1.5)) / numStuds
    const z1 = t1 * (hd - 0.5)
    const z2 = t2 * (hd - 0.5)
    const midZ = (z1 + z2) / 2
    for (const side of [-1, 1]) {
      // Diagonal brace (approximated as tilted box)
      const braceC = pos.clone()
        .addScaledVector(up, foundH + wallH * 0.5)
        .addScaledVector(right, side * (hw + 0.02))
        .addScaledVector(fwd, midZ)
      addBox(g, braceC, up, right, fwd, 0.04, wallH * 0.2, Math.abs(z2 - z1) * 0.4, trimCol)
    }
  }

  // Gable-end framing
  for (const side of [-1, 1]) {
    const gPostC = pos.clone()
      .addScaledVector(up, foundH + wallH / 2)
      .addScaledVector(fwd, side * (hd + 0.02))
    addBox(g, gPostC, up, fwd, right, 0.06, wallH / 2, 0.08, trimCol)
    const gBeamC = pos.clone()
      .addScaledVector(up, foundH + wallH)
      .addScaledVector(fwd, side * (hd + 0.02))
    addBox(g, gBeamC, up, fwd, right, 0.07, 0.08, hw, trimCol)
  }

  // ── Exposed rafter tails ──
  const rafterSpacing = 0.9
  const numRafters = Math.floor(info.depth / rafterSpacing)
  for (let i = 0; i < numRafters; i++) {
    const rz = -hd + (i + 0.5) * rafterSpacing
    for (const side of [-1, 1]) {
      const rc = pos.clone()
        .addScaledVector(up, foundH + wallH - 0.04)
        .addScaledVector(right, side * (hw + roofOverhang * 0.5))
        .addScaledVector(fwd, rz)
      addBox(g, rc, up, right, fwd, roofOverhang * 0.35, 0.04, 0.035, trimCol)
    }
  }

  // ── Roof with ridge cap ──
  const roofBase = pos.clone().addScaledVector(up, foundH + wallH)
  addRoofWedge(g, roofBase, up, right, fwd, hw + roofOverhang, roofRise, hd + roofOverhang, roofCol)
  const ridgeC = pos.clone().addScaledVector(up, foundH + wallH + roofRise)
  addBox(g, ridgeC, up, right, fwd, 0.14, 0.12, hd + roofOverhang * 0.5, jitterColor(roofCol, 0.05, seed + 50))

  // ── Tall wagon doors on long sides (threshing floor) ──
  for (const side of [-1, 1]) {
    const doorH = 1.8
    const doorC = pos.clone()
      .addScaledVector(up, foundH + doorH)
      .addScaledVector(right, side * (hw + 0.04))
    addBox(g, doorC, up, right, fwd, 0.05, doorH, 1.6, darkCol)
    // Door planks
    for (const dsx of [-0.6, 0.6]) {
      const dpC = doorC.clone()
        .addScaledVector(right, -side * 0.02)
        .addScaledVector(fwd, dsx)
      addBox(g, dpC, up, right, fwd, 0.02, doorH - 0.02, 0.55, doorCol, 0.06, seed + 30 + side * 5 + dsx * 3)
    }
    // Door lintel (heavy timber)
    const dlC = pos.clone()
      .addScaledVector(up, foundH + doorH * 2 + 0.06)
      .addScaledVector(right, side * (hw + 0.04))
    addBox(g, dlC, up, right, fwd, 0.07, 0.06, 1.7, trimCol)
    // Stone threshold
    const dtC = pos.clone()
      .addScaledVector(up, foundH + 0.03)
      .addScaledVector(right, side * (hw + 0.12))
    addBox(g, dtC, up, right, fwd, 0.14, 0.04, 1.7, stoneCol)
  }

  // ── Ventilation slits with lintels ──
  const numSlits = 5
  for (let i = 0; i < numSlits; i++) {
    const t = -1 + (2 * (i + 0.5)) / numSlits
    const sz = t * (hd - 1.5)
    for (const side of [-1, 1]) {
      const sc = pos.clone()
        .addScaledVector(up, foundH + wallH * 0.85)
        .addScaledVector(right, side * (hw + 0.03))
        .addScaledVector(fwd, sz)
      addBox(g, sc, up, right, fwd, 0.04, 0.12, 0.4, darkCol)
      // Lintel
      const slC = sc.clone().addScaledVector(up, 0.15)
      addBox(g, slC, up, right, fwd, 0.05, 0.03, 0.44, trimCol)
    }
  }

  // ── Owl hole (small opening high on gable) ──
  for (const side of [-1, 1]) {
    const owlC = pos.clone()
      .addScaledVector(up, foundH + wallH + roofRise * 0.35)
      .addScaledVector(fwd, side * (hd + 0.03))
    addBox(g, owlC, up, right, fwd, 0.3, 0.25, 0.04, darkCol)
  }

  void rng
}

function buildTradingPost(g: GeoArrays, village: VillagePlacement, structure: Structure) {
  // Market hall: open ground floor with pillars, enclosed upper storey
  const info = STRUCTURE_INFO.trading_post
  const seed = structure.localX * 241 + structure.localZ * 359
  const { position: pos, upVec: up, rightVec: right, forwardVec: fwd, rng } =
    buildingBasis(village, structure, seed)

  const hw = info.width / 2
  const hd = info.depth / 2
  const wallH = info.wallHeight
  const foundH = 0.3
  const roofPitch = info.roofPitch * DEG2RAD
  const roofRise = Math.tan(roofPitch) * hw
  const roofOverhang = 0.4
  const lowerH = 3.5
  const upperH = wallH - lowerH + 1.0
  const jetty = 0.35

  const wallCol = varyColor(hexToRGB(info.wallColor), seed, 0.06)
  const wallColDark = weatherDarken(wallCol, 0.3, 0.12)
  const roofCol = varyColor(hexToRGB(info.roofColor), seed + 1, 0.05)
  const trimCol = varyColor(hexToRGB(info.trimColor), seed + 2, 0.04)
  const stoneCol = varyColor([0.50, 0.48, 0.44] as [number, number, number], seed + 3, 0.05)
  const darkCol: [number, number, number] = [0.10, 0.08, 0.06]
  const sillCol: [number, number, number] = [0.50, 0.48, 0.44]

  // ── Foundation platform (stone, with steps, ground AO) ──
  const foundCenter = pos.clone().addScaledVector(up, foundH / 2)
  addBox(g, foundCenter, up, right, fwd, hw + 0.20, foundH / 2, hd + 0.20, aoColor(stoneCol, 0.6), 0.08, seed)
  // Step at front
  const stepC = pos.clone()
    .addScaledVector(up, foundH * 0.3)
    .addScaledVector(fwd, hd + 0.35)
  addBox(g, stepC, up, right, fwd, hw * 0.6, foundH * 0.3, 0.20, stoneCol, 0.06, seed + 5)

  // ── Ground floor pillars (stone bases, timber posts) ──
  const numPillars = 4
  for (let i = 0; i < numPillars; i++) {
    const t = -1 + (2 * (i + 0.5)) / numPillars
    const pz = t * (hd - 0.8)
    for (const side of [-1, 1]) {
      // Stone base
      const sbC = pos.clone()
        .addScaledVector(up, foundH + 0.15)
        .addScaledVector(right, side * (hw - 0.2))
        .addScaledVector(fwd, pz)
      addBox(g, sbC, up, right, fwd, 0.25, 0.15, 0.25, stoneCol, 0.05, seed + i)
      // Timber post
      const pc = pos.clone()
        .addScaledVector(up, foundH + 0.15 + (lowerH - 0.15) / 2)
        .addScaledVector(right, side * (hw - 0.2))
        .addScaledVector(fwd, pz)
      addBox(g, pc, up, right, fwd, 0.18, (lowerH - 0.15) / 2, 0.18, trimCol)
      // Arch brace (decorative bracket at top of post)
      const abC = pos.clone()
        .addScaledVector(up, foundH + lowerH - 0.35)
        .addScaledVector(right, side * (hw - 0.2))
        .addScaledVector(fwd, pz + (side > 0 ? -0.3 : 0.3))
      addBox(g, abC, up, right, fwd, 0.05, 0.30, 0.25, trimCol)
    }
  }

  // ── Floor platform (jetty beam) ──
  const floorC = pos.clone().addScaledVector(up, foundH + lowerH)
  addBox(g, floorC, up, right, fwd, hw + jetty + 0.03, 0.12, hd + jetty + 0.03, trimCol)

  // ── Jetty brackets ──
  const numBrackets = 6
  for (let i = 0; i < numBrackets; i++) {
    const t = -1 + (2 * (i + 0.5)) / numBrackets
    const bz = t * (hd - 0.6)
    for (const side of [-1, 1]) {
      const bc = pos.clone()
        .addScaledVector(up, foundH + lowerH - 0.12)
        .addScaledVector(right, side * (hw + jetty * 0.4))
        .addScaledVector(fwd, bz)
      addBox(g, bc, up, right, fwd, jetty * 0.35, 0.10, 0.06, trimCol)
    }
  }

  // ── Enclosed upper storey with weathering ──
  const upperCenter = pos.clone().addScaledVector(up, foundH + lowerH + upperH / 2)
  const upperLowerH = upperH * 0.4
  const upperUpperH = upperH * 0.6
  const uLC = pos.clone().addScaledVector(up, foundH + lowerH + upperLowerH / 2)
  addBox(g, uLC, up, right, fwd, hw + jetty, upperLowerH / 2, hd + jetty, wallColDark, 0.04, seed + 1)
  const uUC = pos.clone().addScaledVector(up, foundH + lowerH + upperLowerH + upperUpperH / 2)
  addBox(g, uUC, up, right, fwd, hw + jetty, upperUpperH / 2, hd + jetty, wallCol, 0.03, seed + 2)

  // ── Half-timber framing on upper ──
  // Vertical studs
  const numStuds = 5
  for (let i = 0; i < numStuds; i++) {
    const t = -1 + (2 * (i + 0.5)) / numStuds
    const tz = t * (hd + jetty - 0.5)
    for (const side of [-1, 1]) {
      const tc = upperCenter.clone()
        .addScaledVector(right, side * (hw + jetty + 0.02))
        .addScaledVector(fwd, tz)
      addBox(g, tc, up, right, fwd, 0.05, upperH / 2, 0.07, trimCol)
    }
  }
  // Mid-rail
  for (const side of [-1, 1]) {
    const mrC = upperCenter.clone()
      .addScaledVector(right, side * (hw + jetty + 0.02))
    addBox(g, mrC, up, right, fwd, 0.05, 0.05, hd + jetty, trimCol)
  }
  // Eaves beam
  for (const side of [-1, 1]) {
    const ebC = pos.clone()
      .addScaledVector(up, foundH + lowerH + upperH)
      .addScaledVector(right, side * (hw + jetty + 0.02))
    addBox(g, ebC, up, right, fwd, 0.06, 0.07, hd + jetty + 0.03, trimCol)
  }
  // Corner posts
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const cpC = upperCenter.clone()
        .addScaledVector(right, sx * (hw + jetty))
        .addScaledVector(fwd, sz * (hd + jetty))
      addBox(g, cpC, up, right, fwd, 0.08, upperH / 2, 0.08, trimCol)
    }
  }
  // Diagonal braces
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      const dbC = upperCenter.clone()
        .addScaledVector(up, -upperH * 0.15)
        .addScaledVector(right, sx * (hw + jetty + 0.02))
        .addScaledVector(fwd, sz * (hd + jetty - 0.5))
      addBox(g, dbC, up, right, fwd, 0.04, upperH * 0.2, 0.3, trimCol)
    }
  }
  // Gable-end framing
  for (const side of [-1, 1]) {
    const gpC = upperCenter.clone().addScaledVector(fwd, side * (hd + jetty + 0.02))
    addBox(g, gpC, up, fwd, right, 0.05, upperH / 2, 0.07, trimCol)
    const gbC = pos.clone()
      .addScaledVector(up, foundH + lowerH + upperH)
      .addScaledVector(fwd, side * (hd + jetty + 0.02))
    addBox(g, gbC, up, fwd, right, 0.06, 0.07, hw + jetty, trimCol)
  }

  // ── Upper floor windows with sills, lintels, shutters ──
  for (let i = 0; i < numStuds - 1; i++) {
    const t = -1 + (2 * (i + 1.0)) / numStuds
    const wz = t * (hd + jetty - 0.5)
    for (const side of [-1, 1]) {
      const wc = upperCenter.clone()
        .addScaledVector(right, side * (hw + jetty + 0.03))
        .addScaledVector(fwd, wz)
      addBox(g, wc, up, right, fwd, 0.05, 0.25, 0.20, darkCol)
      // Sill
      const sC = wc.clone().addScaledVector(up, -0.28).addScaledVector(right, side * 0.03)
      addBox(g, sC, up, right, fwd, 0.06, 0.025, 0.24, sillCol)
      // Lintel
      const lC = wc.clone().addScaledVector(up, 0.28)
      addBox(g, lC, up, right, fwd, 0.06, 0.04, 0.26, trimCol)
      // Shutter
      if (rng() < 0.5) {
        const shC = wc.clone()
          .addScaledVector(right, side * 0.08)
          .addScaledVector(fwd, 0.22)
        addBox(g, shC, up, right, fwd, 0.04, 0.24, 0.015, trimCol)
      }
    }
  }

  // ── Exposed rafter tails ──
  const rafterSpacing = 0.9
  const numRafters = Math.floor((info.depth + jetty * 2) / rafterSpacing)
  for (let i = 0; i < numRafters; i++) {
    const rz = -(hd + jetty) + (i + 0.5) * rafterSpacing
    for (const side of [-1, 1]) {
      const rc = pos.clone()
        .addScaledVector(up, foundH + lowerH + upperH - 0.04)
        .addScaledVector(right, side * (hw + jetty + roofOverhang * 0.5))
        .addScaledVector(fwd, rz)
      addBox(g, rc, up, right, fwd, roofOverhang * 0.35, 0.035, 0.03, trimCol)
    }
  }

  // ── Roof with ridge cap ──
  const roofBase = pos.clone().addScaledVector(up, foundH + lowerH + upperH)
  addRoofWedge(g, roofBase, up, right, fwd, hw + jetty + roofOverhang, roofRise, hd + jetty + roofOverhang, roofCol)
  const ridgeC = pos.clone().addScaledVector(up, foundH + lowerH + upperH + roofRise)
  addBox(g, ridgeC, up, right, fwd, 0.14, 0.11, hd + jetty + roofOverhang * 0.5, jitterColor(roofCol, 0.05, seed + 50))

  // ── Goods counter / stall on ground floor ──
  const counterC = pos.clone()
    .addScaledVector(up, foundH + 0.45)
    .addScaledVector(fwd, hd - 0.5)
  addBox(g, counterC, up, right, fwd, hw * 0.7, 0.45, 0.25, trimCol)
  // Counter top
  const ctC = counterC.clone().addScaledVector(up, 0.47)
  addBox(g, ctC, up, right, fwd, hw * 0.72, 0.03, 0.28, jitterColor(trimCol, 0.06, seed + 60))
}

// ─── Standalone builders for non-structure elements ─────────────────

function buildPasture(g: GeoArrays, village: VillagePlacement, structure: Structure) {
  const info = STRUCTURE_INFO.pasture
  const rx = info.width / 2, rz = info.depth / 2
  const NUM_POSTS = 28
  const POST_HEIGHT = 2.0
  const POST_WIDTH = 0.2
  const RAIL_THICKNESS = 0.12
  const woodCol: [number, number, number] = [0.545, 0.412, 0.078]
  const hayCol: [number, number, number] = [0.75, 0.60, 0.20]
  const troughCol: [number, number, number] = [0.40, 0.30, 0.15]

  const seed = structure.localX * 137 + structure.localZ * 251

  // Generate irregular perimeter
  const perimeterPosts: Array<{ lx: number; lz: number }> = []
  for (let i = 0; i < NUM_POSTS; i++) {
    const angle = (i / NUM_POSTS) * Math.PI * 2
    const noise = Math.sin(angle * 3.7 + seed * 0.1) * 0.15 +
                  Math.sin(angle * 5.3 + seed * 0.07) * 0.1 +
                  Math.sin(angle * 2.1 + seed * 0.13) * 0.12
    const r = 1 + noise
    const lx = structure.localX + Math.cos(angle) * rx * r
    const lz = structure.localZ + Math.sin(angle) * rz * r
    perimeterPosts.push({ lx, lz })
  }

  for (let i = 0; i < perimeterPosts.length; i++) {
    const p = perimeterPosts[i]
    const next = perimeterPosts[(i + 1) % perimeterPosts.length]

    const { position: pos, normal } = localToWorld(village, p.lx, p.lz)
    const postCenter = pos.clone().addScaledVector(normal, POST_HEIGHT / 2)
    const arbitrary = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
    const postRight = new THREE.Vector3().crossVectors(normal, arbitrary).normalize()
    const postFwd = new THREE.Vector3().crossVectors(postRight, normal).normalize()
    addBox(g, postCenter, normal, postRight, postFwd, POST_WIDTH / 2, POST_HEIGHT / 2, POST_WIDTH / 2, woodCol, 0.05, seed + i)

    const { position: nextPos, normal: nextNormal } = localToWorld(village, next.lx, next.lz)
    for (const railH of [0.6, 1.3]) {
      const from = pos.clone().addScaledVector(normal, railH)
      const to = nextPos.clone().addScaledVector(nextNormal, railH)
      const mid = new THREE.Vector3().lerpVectors(from, to, 0.5)
      const dir = new THREE.Vector3().subVectors(to, from)
      const len = dir.length()
      dir.normalize()
      const railUp = mid.clone().normalize()
      const railRight = new THREE.Vector3().crossVectors(railUp, dir).normalize()
      addBox(g, mid, railUp, dir, railRight, len / 2, RAIL_THICKNESS / 2, RAIL_THICKNESS / 2, woodCol)
    }
  }

  // Hay bales
  const numBales = 3 + Math.floor(Math.abs(seed) % 4)
  for (let i = 0; i < numBales; i++) {
    const bAngle = (i / numBales) * Math.PI * 2 + seed * 0.3
    const bDist = 0.3 + (Math.sin(i * 7.3 + seed) * 0.5 + 0.5) * 0.4
    const blx = structure.localX + Math.cos(bAngle) * rx * bDist
    const blz = structure.localZ + Math.sin(bAngle) * rz * bDist
    const { position: balePos, normal: baleN } = localToWorld(village, blx, blz)
    const baleCenter = balePos.clone().addScaledVector(baleN, 0.4)
    const bArb = Math.abs(baleN.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
    const bRight = new THREE.Vector3().crossVectors(baleN, bArb).normalize()
    const bFwd = new THREE.Vector3().crossVectors(bRight, baleN).normalize()
    addBox(g, baleCenter, baleN, bRight, bFwd, 0.5, 0.4, 0.5, hayCol, 0.06, seed + i * 13)
  }

  // Water trough
  const { position: troughPos, normal: troughN } = localToWorld(
    village, structure.localX + rx * 0.15, structure.localZ + rz * 0.15
  )
  const troughCenter = troughPos.clone().addScaledVector(troughN, 0.3)
  const tArb = Math.abs(troughN.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
  const tRight = new THREE.Vector3().crossVectors(troughN, tArb).normalize()
  const tFwd = new THREE.Vector3().crossVectors(tRight, troughN).normalize()
  addBox(g, troughCenter, troughN, tRight, tFwd, 1.2, 0.3, 0.4, troughCol, 0.05, seed + 99)
}

function buildProp(g: GeoArrays, village: VillagePlacement, prop: Prop) {
  const { position: pos, normal } = localToWorld(village, prop.localX, prop.localZ)
  const upVec = normal.clone()
  const arbitrary = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
  let rightVec = new THREE.Vector3().crossVectors(upVec, arbitrary).normalize()
  let forwardVec = new THREE.Vector3().crossVectors(rightVec, upVec).normalize()

  const cosY = Math.cos(prop.rotation)
  const sinY = Math.sin(prop.rotation)
  const newR = rightVec.clone().multiplyScalar(cosY).add(forwardVec.clone().multiplyScalar(sinY))
  const newF = forwardVec.clone().multiplyScalar(cosY).sub(rightVec.clone().multiplyScalar(sinY))
  rightVec = newR.normalize()
  forwardVec = newF.normalize()

  const up = upVec
  const right = rightVec
  const fwd = forwardVec

  switch (prop.type) {
      case 'well': {
        // Stone cylinder (0.75m radius, 0.9m tall)
        const wellBase = pos.clone().addScaledVector(up, 0.45)
        const stoneCol: [number, number, number] = [0.55, 0.55, 0.52]
        addPrism(g, wellBase, up, right, fwd, 0.75, 0.75, 0.45, 10, stoneCol)
        // Two wooden posts
        const woodCol: [number, number, number] = [0.35, 0.22, 0.12]
        for (const sx of [-1, 1]) {
          const postC = pos.clone().addScaledVector(up, 1.0).addScaledVector(right, sx * 0.55)
          addBox(g, postC, up, right, fwd, 0.05, 1.0, 0.05, woodCol)
        }
        // Crossbeam
        const beamC = pos.clone().addScaledVector(up, 2.0)
        addBox(g, beamC, up, right, fwd, 0.6, 0.05, 0.05, woodCol)
        // Small peaked roof
        const roofBase = pos.clone().addScaledVector(up, 2.05)
        addRoofWedge(g, roofBase, up, right, fwd, 0.7, 0.5, 0.6, woodCol)
        break
      }
      case 'barrel': {
        // Cylinder (0.35m radius, 0.85m tall), brown
        const barrelCol: [number, number, number] = [0.45, 0.28, 0.15]
        const barrelC = pos.clone().addScaledVector(up, 0.425)
        addPrism(g, barrelC, up, right, fwd, 0.35, 0.35, 0.425, 8, barrelCol)
        // Metal bands (slightly larger rings)
        const bandCol: [number, number, number] = [0.3, 0.3, 0.28]
        for (const h of [0.15, 0.7]) {
          const bandC = pos.clone().addScaledVector(up, h)
          addPrism(g, bandC, up, right, fwd, 0.37, 0.37, 0.03, 8, bandCol)
        }
        break
      }
      case 'woodpile': {
        // Box stack (1.2m x 0.6m x 0.8m), pale tan
        const woodCol: [number, number, number] = [0.72, 0.58, 0.38]
        const wc = pos.clone().addScaledVector(up, 0.4)
        addBox(g, wc, up, right, fwd, 0.6, 0.4, 0.3, woodCol)
        // Second layer slightly offset
        const wc2 = pos.clone().addScaledVector(up, 0.7).addScaledVector(right, 0.05)
        addBox(g, wc2, up, right, fwd, 0.5, 0.15, 0.25, woodCol)
        break
      }
      case 'haystack': {
        // Cone on cylinder (1.5m radius, 1.2m cylinder + 1m cone), golden
        const hayCol: [number, number, number] = [0.906, 0.655, 0.071]
        const cylC = pos.clone().addScaledVector(up, 0.6)
        addPrism(g, cylC, up, right, fwd, 1.5, 1.5, 0.6, 10, hayCol)
        const coneC = pos.clone().addScaledVector(up, 1.2 + 0.5)
        addCone(g, coneC, up, right, fwd, 1.5, 0.5, 10, hayCol)
        break
      }
      case 'cart': {
        // Box body (1.6m x 0.9m x 0.5m) on wheels
        const woodCol: [number, number, number] = [0.45, 0.32, 0.18]
        const bodyC = pos.clone().addScaledVector(up, 0.75)
        addBox(g, bodyC, up, right, fwd, 0.8, 0.25, 0.45, woodCol)
        // Wheels (two cylinders 0.5m radius on sides)
        const wheelCol: [number, number, number] = [0.3, 0.22, 0.12]
        for (const sx of [-1, 1]) {
          const wheelC = pos.clone().addScaledVector(up, 0.5).addScaledVector(right, sx * 0.55)
          addPrism(g, wheelC, right, up, fwd, 0.5, 0.5, 0.06, 10, wheelCol)
        }
        // Shafts (thin boxes extending forward)
        for (const sx of [-0.3, 0.3]) {
          const shaftC = pos.clone()
            .addScaledVector(up, 0.65)
            .addScaledVector(right, sx)
            .addScaledVector(fwd, 1.1)
          addBox(g, shaftC, up, right, fwd, 0.03, 0.03, 0.7, woodCol)
        }
        break
      }
      case 'crate': {
        // Small box (0.5m cube), pale wood
        const crateCol: [number, number, number] = [0.68, 0.55, 0.35]
        const cc = pos.clone().addScaledVector(up, 0.25)
        addBox(g, cc, up, right, fwd, 0.25, 0.25, 0.25, crateCol)
        break
      }
      case 'trough': {
        // Long box (1.5m x 0.5m x 0.4m), dark wood
        const troughCol: [number, number, number] = [0.3, 0.2, 0.1]
        const tc = pos.clone().addScaledVector(up, 0.2)
        addBox(g, tc, up, right, fwd, 0.75, 0.2, 0.25, troughCol)
        // Legs
        for (const sx of [-0.5, 0.5]) {
          const legC = pos.clone().addScaledVector(up, 0.1).addScaledVector(fwd, sx)
          addBox(g, legC, up, right, fwd, 0.04, 0.1, 0.04, troughCol)
        }
        break
      }
  }
}

function buildPath(g: GeoArrays, village: VillagePlacement, path: PathSegment, seed: number) {
  const rng = mulberry32(seed)
  const dx = path.toX - path.fromX
  const dz = path.toZ - path.fromZ
  const pathLen = Math.sqrt(dx * dx + dz * dz)
  if (pathLen < 1) return

  const dirX = dx / pathLen, dirZ = dz / pathLen
  const perpX = -dirZ, perpZ = dirX

  const STONE_SPACING = 0.8
  const numStones = Math.floor(pathLen / STONE_SPACING)

  for (let i = 0; i < numStones; i++) {
    const t = (i + 0.5) / numStones
    const tJitter = t + (rng() - 0.5) * 0.6 / numStones
    const lx = path.fromX + dx * tJitter
    const lz = path.fromZ + dz * tJitter

    const lateralOffset = (rng() - 0.5) * 1.8
    const stoneX = lx + perpX * lateralOffset
    const stoneZ = lz + perpZ * lateralOffset

    const { position, normal } = localToWorld(village, stoneX, stoneZ)
    const center = position.clone().addScaledVector(normal, 0.06)

    const hw = 0.2 + rng() * 0.35
    const hd = 0.2 + rng() * 0.35
    const hh = 0.03 + rng() * 0.04

    const rot = rng() * Math.PI * 2
    const cosR = Math.cos(rot), sinR = Math.sin(rot)

    const arbitrary = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
    const tx = new THREE.Vector3().crossVectors(normal, arbitrary).normalize()
    const tz = new THREE.Vector3().crossVectors(tx, normal).normalize()

    const rxX = tx.x * cosR + tz.x * sinR
    const rxY = tx.y * cosR + tz.y * sinR
    const rxZ = tx.z * cosR + tz.z * sinR
    const rzX = -tx.x * sinR + tz.x * cosR
    const rzY = -tx.y * sinR + tz.y * cosR
    const rzZ = -tx.z * sinR + tz.z * cosR

    const baseIdx = g.positions.length / 3
    for (const [sx, sz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]] as const) {
      const px = center.x + rxX * hw * sx + rzX * hd * sz + normal.x * hh
      const py = center.y + rxY * hw * sx + rzY * hd * sz + normal.y * hh
      const pz = center.z + rxZ * hw * sx + rzZ * hd * sz + normal.z * hh
      g.positions.push(px, py, pz)

      const shade = 0.65 + rng() * 0.2
      g.colors.push(shade * 0.95, shade * 0.93, shade * 0.88)
    }

    // Reverse winding so face normal points outward (away from sphere center)
    // The tangent basis produces rz×rx = +normal, but THREE.js (C-B)×(A-B)
    // with the original winding gives -normal. Reversed winding fixes this.
    g.indices.push(baseIdx, baseIdx + 2, baseIdx + 1, baseIdx, baseIdx + 3, baseIdx + 2)
  }
}

function buildFence(g: GeoArrays, village: VillagePlacement) {
  const posts = village.layout.fencePosts
  if (posts.length < 3) return

  const POST_HEIGHT = 2.2
  const POST_WIDTH = 0.25
  const RAIL_HEIGHT_1 = 0.7
  const RAIL_HEIGHT_2 = 1.5
  const RAIL_THICKNESS = 0.12
  const MAX_RAIL_SPAN = 8
  const woodCol: [number, number, number] = [0.545, 0.412, 0.078]

  for (let i = 0; i < posts.length; i++) {
    const p = posts[i]
    const { position: pos, normal } = localToWorld(village, p.localX, p.localZ)

    const postCenter = pos.clone().addScaledVector(normal, POST_HEIGHT / 2)
    const arbitrary = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
    const postRight = new THREE.Vector3().crossVectors(normal, arbitrary).normalize()
    const postFwd = new THREE.Vector3().crossVectors(postRight, normal).normalize()
    addBox(g, postCenter, normal, postRight, postFwd, POST_WIDTH / 2, POST_HEIGHT / 2, POST_WIDTH / 2, woodCol, 0.05, i * 7.3)

    const next = posts[(i + 1) % posts.length]
    const postDist = Math.sqrt((next.localX - p.localX) ** 2 + (next.localZ - p.localZ) ** 2)
    if (postDist > MAX_RAIL_SPAN) continue

    const { position: nextPos, normal: nextNormal } = localToWorld(village, next.localX, next.localZ)

    for (const railH of [RAIL_HEIGHT_1, RAIL_HEIGHT_2]) {
      const from = pos.clone().addScaledVector(normal, railH)
      const to = nextPos.clone().addScaledVector(nextNormal, railH)
      const mid = new THREE.Vector3().lerpVectors(from, to, 0.5)
      const dir = new THREE.Vector3().subVectors(to, from)
      const len = dir.length()
      dir.normalize()

      const railUp = mid.clone().normalize()
      const railRight = new THREE.Vector3().crossVectors(railUp, dir).normalize()

      addBox(g, mid, railUp, dir, railRight, len / 2, RAIL_THICKNESS / 2, RAIL_THICKNESS / 2, woodCol)
    }
  }
}

// ─── Street lamp geometry ────────────────────────────────────────────

function buildLampPost(
  g: GeoArrays,
  glow: GeoArrays,
  village: VillagePlacement,
  localX: number,
  localZ: number,
) {
  const { position: pos, normal } = localToWorld(village, localX, localZ)
  const up = normal.clone()
  const arbitrary = Math.abs(normal.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
  const right = new THREE.Vector3().crossVectors(up, arbitrary).normalize()
  const fwd = new THREE.Vector3().crossVectors(right, up).normalize()

  const postCol: [number, number, number] = [0.22, 0.18, 0.14]
  const ironCol: [number, number, number] = [0.18, 0.16, 0.14]
  const lanternCol: [number, number, number] = [0.95, 0.75, 0.35]
  const capCol: [number, number, number] = [0.20, 0.18, 0.15]

  const postH = 3.2

  // Wooden post
  const postC = pos.clone().addScaledVector(up, postH / 2)
  addBox(g, postC, up, right, fwd, 0.07, postH / 2, 0.07, postCol)

  // Iron bracket arm extending to one side
  const armC = pos.clone()
    .addScaledVector(up, postH - 0.15)
    .addScaledVector(right, 0.35)
  addBox(g, armC, up, right, fwd, 0.35, 0.04, 0.04, ironCol)

  // Diagonal brace
  const braceC = pos.clone()
    .addScaledVector(up, postH - 0.55)
    .addScaledVector(right, 0.25)
  addBox(g, braceC, up, right, fwd, 0.2, 0.3, 0.03, ironCol)

  // Lantern body — built into the emissive glow mesh
  const lanternC = pos.clone()
    .addScaledVector(up, postH - 0.55)
    .addScaledVector(right, 0.6)
  addBox(glow, lanternC, up, right, fwd, 0.12, 0.18, 0.12, lanternCol)

  // Lantern cap (small pyramid top)
  const capC = lanternC.clone().addScaledVector(up, 0.22)
  addBox(g, capC, up, right, fwd, 0.15, 0.04, 0.15, capCol)
}

/** Compute lamp post positions spaced evenly along all roads. */
function computeLampPositions(
  village: VillagePlacement,
): Array<{ localX: number; localZ: number; worldPos: THREE.Vector3 }> {
  const LAMP_SPACING = 30
  const ROAD_OFFSET = 2.5
  const lamps: Array<{ localX: number; localZ: number; worldPos: THREE.Vector3 }> = []
  let accumDist = 0
  let side = 1

  for (const path of village.layout.paths) {
    const dx = path.toX - path.fromX
    const dz = path.toZ - path.fromZ
    const pathLen = Math.sqrt(dx * dx + dz * dz)
    if (pathLen < 1) continue

    const perpX = -dz / pathLen
    const perpZ = dx / pathLen

    let d = LAMP_SPACING - accumDist
    while (d < pathLen) {
      const frac = d / pathLen
      const lx = path.fromX + dx * frac + perpX * ROAD_OFFSET * side
      const lz = path.fromZ + dz * frac + perpZ * ROAD_OFFSET * side
      const { position } = localToWorld(village, lx, lz)
      // Raise light to lantern height
      const normal = position.clone().normalize()
      const lightPos = position.clone().addScaledVector(normal, 2.65)
      lamps.push({ localX: lx, localZ: lz, worldPos: lightPos })
      side *= -1
      d += LAMP_SPACING
    }

    accumDist = (accumDist + pathLen) % LAMP_SPACING
  }

  return lamps
}

// ─── Dock geometry ──────────────────────────────────────────────────

function buildDock(g: GeoArrays, village: VillagePlacement, structure: Structure) {
  const info = STRUCTURE_INFO.dock
  const seed = structure.localX * 137 + structure.localZ * 251
  const { position: pos, upVec: up, rightVec: right, forwardVec: fwd } =
    buildingBasis(village, structure, seed)

  const wallCol = varyColor(hexToRGB(info.wallColor), seed, 0.06)
  const roofCol = varyColor(hexToRGB(info.roofColor), seed + 1, 0.05)
  const trimCol = varyColor(hexToRGB(info.trimColor), seed + 2, 0.04)
  const plankCol: [number, number, number] = varyColor([0.45, 0.36, 0.22], seed + 3, 0.06)
  const pileCol: [number, number, number] = varyColor([0.35, 0.28, 0.18], seed + 4, 0.05)

  // ── Small hut (at the "land" end) ──
  const hw = info.width / 2
  const hd = info.depth / 2
  const wallH = info.wallHeight
  const foundH = 0.25
  const roofPitch = info.roofPitch * DEG2RAD
  const roofRise = Math.tan(roofPitch) * hw

  // Foundation
  const foundCenter = pos.clone().addScaledVector(up, foundH / 2)
  addBox(g, foundCenter, up, right, fwd, hw + 0.15, foundH / 2, hd + 0.15,
    aoColor(plankCol, 0.6), 0.04, seed)

  // Walls
  const wallCenter = pos.clone().addScaledVector(up, foundH + wallH / 2)
  addBox(g, wallCenter, up, right, fwd, hw, wallH / 2, hd,
    weatherDarken(wallCol, 0.5, 0.12), 0.04, seed + 10)

  // Roof
  const roofBase = pos.clone().addScaledVector(up, foundH + wallH)
  addRoofWedge(g, roofBase, up, right, fwd, hw + 0.3, roofRise, hd + 0.3, roofCol)

  // Door (facing the pier)
  const doorH = 1.6, doorW = 0.8
  const doorCenter = pos.clone()
    .addScaledVector(up, foundH + doorH / 2)
    .addScaledVector(fwd, hd + 0.02)
  const darkCol: [number, number, number] = [0.22, 0.16, 0.10]
  addBox(g, doorCenter, up, right, fwd, doorW / 2, doorH / 2, 0.05, darkCol)

  // Timber frame accents
  const postH = wallH + foundH
  for (const sx of [-1, 1]) {
    const cornerPos = pos.clone()
      .addScaledVector(up, postH / 2)
      .addScaledVector(right, hw * sx)
      .addScaledVector(fwd, hd)
    addBox(g, cornerPos, up, right, fwd, 0.08, postH / 2, 0.08, trimCol)
    const backCorner = pos.clone()
      .addScaledVector(up, postH / 2)
      .addScaledVector(right, hw * sx)
      .addScaledVector(fwd, -hd)
    addBox(g, backCorner, up, right, fwd, 0.08, postH / 2, 0.08, trimCol)
  }

  // ── Pier / dock extending forward (along fwd direction) ──
  // Sample along forward direction to find where ocean starts, then extend far past.
  // sampleTerrainRadius returns RADIUS (10000) at ocean (heightAt <= 0).
  // Land values are > 10000, so we detect ocean when radius <= RADIUS + small epsilon.
  const OCEAN_EXTEND = 40
  const SAMPLE_STEP = 2
  const MAX_PROBE = 80
  const OCEAN_R = 10000
  const OCEAN_EPSILON = 0.5  // anything within 0.5 of RADIUS is ocean
  let distToOcean = MAX_PROBE // fallback: extend full length
  for (let probe = 0; probe < MAX_PROBE; probe += SAMPLE_STEP) {
    const samplePos = pos.clone().addScaledVector(fwd, hd + 1 + probe)
    const sDir = samplePos.clone().normalize()
    const sR = sampleTerrainRadius(sDir.x, sDir.y, sDir.z)
    if (sR <= OCEAN_R + OCEAN_EPSILON) {
      distToOcean = probe
      break
    }
  }
  const pierLength = distToOcean + OCEAN_EXTEND
  const pierWidth = 2.5
  const pierHeight = 1.8
  const numPilings = Math.floor(pierLength / 3.5) + 1
  const pierStart = hd + 1.0

  // Pilings (vertical posts going down to ground/seabed)
  for (let pi = 0; pi < numPilings; pi++) {
    const t = pi / (numPilings - 1)
    const pz = pierStart + t * pierLength
    for (const sx of [-1, 1]) {
      // Sample terrain/seabed at this piling position to find ground level
      const pileSamplePos = pos.clone()
        .addScaledVector(fwd, pz)
        .addScaledVector(right, pierWidth / 2 * sx * 0.9)
      const pileSampleDir = pileSamplePos.clone().normalize()
      const groundR = sampleTerrainRadius(pileSampleDir.x, pileSampleDir.y, pileSampleDir.z)
      const deckR = pos.length() + pierHeight // radius of the deck surface
      // Drop = how far below deck the ground/seabed is
      const groundDrop = Math.max(0, deckR - groundR)
      // Piling extends from deck level down to ground, plus a small embed
      const pileHalfH = (groundDrop + 0.3) / 2
      const pileCenterY = pierHeight - pileHalfH

      const pilePos = pos.clone()
        .addScaledVector(fwd, pz)
        .addScaledVector(right, pierWidth / 2 * sx * 0.9)
        .addScaledVector(up, pileCenterY)
      addBox(g, pilePos, up, right, fwd, 0.12, pileHalfH, 0.12,
        pileCol, 0.06, seed + pi * 13 + sx * 7)
    }
  }

  // Deck planking (flat surface)
  const deckCenter = pos.clone()
    .addScaledVector(fwd, pierStart + pierLength / 2)
    .addScaledVector(up, pierHeight - 0.08)
  addBox(g, deckCenter, up, right, fwd, pierWidth / 2, 0.08, pierLength / 2,
    plankCol, 0.03, seed + 50)

  // Side rails
  const railH = 0.8
  for (const sx of [-1, 1]) {
    const railCenter = pos.clone()
      .addScaledVector(fwd, pierStart + pierLength / 2)
      .addScaledVector(right, pierWidth / 2 * sx)
      .addScaledVector(up, pierHeight + railH / 2)
    addBox(g, railCenter, up, right, fwd, 0.06, railH / 2, pierLength / 2,
      pileCol, 0.04, seed + 60 + sx)
  }

  // Mooring posts at the end
  for (const sx of [-1, 1]) {
    const moorPos = pos.clone()
      .addScaledVector(fwd, pierStart + pierLength - 0.5)
      .addScaledVector(right, pierWidth / 2 * sx * 0.7)
      .addScaledVector(up, pierHeight + 0.5)
    addPrism(g, moorPos, up, right, fwd, 0.1, 0.06, 0.5, 6, pileCol)
  }

  // Cross-bracing between pilings for structural detail
  for (let pi = 0; pi < numPilings - 1; pi++) {
    const t0 = pi / (numPilings - 1)
    const t1 = (pi + 1) / (numPilings - 1)
    const pz0 = pierStart + t0 * pierLength
    const pz1 = pierStart + t1 * pierLength
    const braceY = pierHeight * 0.35
    const braceCenter = pos.clone()
      .addScaledVector(fwd, (pz0 + pz1) / 2)
      .addScaledVector(up, braceY)
    addBox(g, braceCenter, up, right, fwd, pierWidth / 2 * 0.85, 0.06, (pz1 - pz0) / 2 * 0.9,
      pileCol, 0.04, seed + 80 + pi)
  }
}

// ─── Farm geometry ──────────────────────────────────────────────────

function buildFarm(g: GeoArrays, village: VillagePlacement, structure: Structure) {
  const info = STRUCTURE_INFO.farm
  const seed = structure.localX * 137 + structure.localZ * 251
  const { position: pos, upVec: up, rightVec: right, forwardVec: fwd, rng } =
    buildingBasis(village, structure, seed)

  const wallCol = varyColor(hexToRGB(info.wallColor), seed, 0.06)
  const roofCol = varyColor(hexToRGB(info.roofColor), seed + 1, 0.05)
  const trimCol = varyColor(hexToRGB(info.trimColor), seed + 2, 0.04)
  const woodCol: [number, number, number] = varyColor([0.545, 0.412, 0.078], seed + 3, 0.05)

  // ── Farmhouse (small, simple) ──
  const hw = info.width / 2
  const hd = info.depth / 2
  const wallH = info.wallHeight
  const foundH = 0.25
  const roofPitch = info.roofPitch * DEG2RAD
  const roofRise = Math.tan(roofPitch) * hw

  const stoneCol: [number, number, number] = varyColor([0.44, 0.42, 0.40], seed + 6, 0.05)
  addBox(g, pos.clone().addScaledVector(up, foundH / 2), up, right, fwd,
    hw + 0.1, foundH / 2, hd + 0.1, aoColor(stoneCol, 0.6), 0.04, seed)
  addBox(g, pos.clone().addScaledVector(up, foundH + wallH / 2), up, right, fwd,
    hw, wallH / 2, hd, weatherDarken(wallCol, 0.4, 0.12), 0.04, seed + 10)

  const postH = wallH + foundH
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      addBox(g, pos.clone().addScaledVector(up, postH / 2).addScaledVector(right, hw * sx).addScaledVector(fwd, hd * sz),
        up, right, fwd, 0.08, postH / 2, 0.08, trimCol)
    }
  }
  addRoofWedge(g, pos.clone().addScaledVector(up, foundH + wallH), up, right, fwd,
    hw + 0.35, roofRise, hd + 0.35, roofCol)
  addBox(g, pos.clone().addScaledVector(up, foundH + 0.75).addScaledVector(fwd, hd + 0.02),
    up, right, fwd, 0.35, 0.75, 0.05, [0.25, 0.18, 0.10])

  // ── Large irregular fenced field (noise-based perimeter like pasture) ──
  const fieldRX = 30 + rng() * 15   // ~30–45 radius X
  const fieldRZ = 35 + rng() * 20   // ~35–55 radius Z
  const NUM_POSTS = 48
  const POST_HEIGHT = 1.8
  const POST_WIDTH = 0.18
  const RAIL_THICKNESS = 0.10

  // Offset the field center behind the house
  const fieldCX = structure.localX + Math.cos(structure.rotation + Math.PI) * (hd + fieldRZ * 0.6)
  const fieldCZ = structure.localZ + Math.sin(structure.rotation + Math.PI) * (hd + fieldRZ * 0.6)

  // Generate irregular perimeter with noise (same technique as pasture)
  const perimeterPosts: Array<{ lx: number; lz: number }> = []
  for (let i = 0; i < NUM_POSTS; i++) {
    const angle = (i / NUM_POSTS) * Math.PI * 2
    const noise = Math.sin(angle * 2.7 + seed * 0.11) * 0.12 +
                  Math.sin(angle * 4.3 + seed * 0.07) * 0.08 +
                  Math.sin(angle * 6.1 + seed * 0.13) * 0.06
    const r = 1 + noise
    const lx = fieldCX + Math.cos(angle) * fieldRX * r
    const lz = fieldCZ + Math.sin(angle) * fieldRZ * r
    perimeterPosts.push({ lx, lz })
  }

  // Build fence: posts + rails
  for (let i = 0; i < perimeterPosts.length; i++) {
    const p = perimeterPosts[i]
    const next = perimeterPosts[(i + 1) % perimeterPosts.length]

    const { position: postPos, normal: postN } = localToWorld(village, p.lx, p.lz)
    const pc = postPos.clone().addScaledVector(postN, POST_HEIGHT / 2)
    const arb = Math.abs(postN.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
    const pRight = new THREE.Vector3().crossVectors(postN, arb).normalize()
    const pFwd = new THREE.Vector3().crossVectors(pRight, postN).normalize()
    addBox(g, pc, postN, pRight, pFwd, POST_WIDTH / 2, POST_HEIGHT / 2, POST_WIDTH / 2,
      woodCol, 0.05, seed + i * 7)

    const { position: nextPos, normal: nextN } = localToWorld(village, next.lx, next.lz)
    for (const railH of [0.5, 1.2]) {
      const from = postPos.clone().addScaledVector(postN, railH)
      const to = nextPos.clone().addScaledVector(nextN, railH)
      const mid = new THREE.Vector3().lerpVectors(from, to, 0.5)
      const dir = new THREE.Vector3().subVectors(to, from)
      const len = dir.length()
      dir.normalize()
      const railUp = mid.clone().normalize()
      const railRight = new THREE.Vector3().crossVectors(railUp, dir).normalize()
      addBox(g, mid, railUp, dir, railRight, len / 2, RAIL_THICKNESS / 2, RAIL_THICKNESS / 2, woodCol)
    }
  }

  // ── Hay bales (scattered near the farmhouse) ──
  const numBales = 2 + Math.floor(rng() * 3)
  const hayCol: [number, number, number] = [0.75, 0.60, 0.20]
  for (let bi = 0; bi < numBales; bi++) {
    const bAngle = rng() * Math.PI * 2
    const bDist = hd + 3 + rng() * 5
    const blx = structure.localX + Math.cos(bAngle) * bDist
    const blz = structure.localZ + Math.sin(bAngle) * bDist
    const { position: balePos, normal: baleN } = localToWorld(village, blx, blz)
    const bArb = Math.abs(baleN.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
    const bRight = new THREE.Vector3().crossVectors(baleN, bArb).normalize()
    const bFwd = new THREE.Vector3().crossVectors(bRight, baleN).normalize()
    addBox(g, balePos.clone().addScaledVector(baleN, 0.45), baleN, bRight, bFwd,
      0.55, 0.45, 0.55, hayCol, 0.06, seed + bi * 17)
  }

  // ── Scarecrow ──
  {
    const scLx = fieldCX + (rng() - 0.5) * fieldRX * 0.6
    const scLz = fieldCZ + (rng() - 0.5) * fieldRZ * 0.6
    const { position: scPos, normal: scN } = localToWorld(village, scLx, scLz)
    const arb = Math.abs(scN.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0)
    const scRight = new THREE.Vector3().crossVectors(scN, arb).normalize()
    const scFwd = new THREE.Vector3().crossVectors(scRight, scN).normalize()
    const poleCol: [number, number, number] = [0.40, 0.30, 0.18]
    const clothCol: [number, number, number] = varyColor([0.60, 0.50, 0.35], seed + 80, 0.08)
    addBox(g, scPos.clone().addScaledVector(scN, 1.5), scN, scRight, scFwd, 0.06, 1.5, 0.06, poleCol)
    addBox(g, scPos.clone().addScaledVector(scN, 2.5), scN, scRight, scFwd, 0.8, 0.06, 0.06, poleCol)
    addBox(g, scPos.clone().addScaledVector(scN, 2.1).addScaledVector(scRight, 0.15), scN, scRight, scFwd, 0.5, 0.35, 0.03, clothCol)
  }
}

// ─── Merged village mesh (1 draw call per village) ──────────────────

const structureBuilders: Record<string, (g: GeoArrays, v: VillagePlacement, s: Structure) => void> = {
  house: buildHouse,
  church: buildChurch,
  castle: buildCastle,
  pub: buildPub,
  stable: buildStable,
  armory: buildArmory,
  barn: buildBarn,
  trading_post: buildTradingPost,
  pasture: buildPasture,
  dock: buildDock,
  farm: buildFarm,
}

// ─── Lantern glow material (emissive self-glow for the glass) ───────

const lanternMaterial = new THREE.MeshStandardMaterial({
  vertexColors: true,
  roughness: 0.3,
  metalness: 0.0,
  emissive: new THREE.Color(0.95, 0.65, 0.2),
  emissiveIntensity: 2.5,
  flatShading: true,
})

// ─── Build village mesh data (pure function, no hooks) ──────────────

interface VillageMeshData {
  geometry: THREE.BufferGeometry | null
  glowGeometry: THREE.BufferGeometry | null
  lampPositions: THREE.Vector3[]
}

function buildVillageMeshData(village: VillagePlacement): VillageMeshData {
  const g = createGeo()
  const glow = createGeo()

  for (const s of village.layout.structures) {
    const builder = structureBuilders[s.type]
    if (builder) builder(g, village, s)
  }
  for (const p of village.layout.props) {
    buildProp(g, village, p)
  }
  for (let pi = 0; pi < village.layout.paths.length; pi++) {
    buildPath(g, village, village.layout.paths[pi], village.seed + pi * 131)
  }
  buildFence(g, village)

  const lamps = computeLampPositions(village)
  for (const lamp of lamps) {
    buildLampPost(g, glow, village, lamp.localX, lamp.localZ)
  }

  if (g.positions.length === 0) return { geometry: null, glowGeometry: null, lampPositions: [] }

  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.Float32BufferAttribute(g.positions, 3))
  geo.setAttribute('color', new THREE.Float32BufferAttribute(g.colors, 3))
  geo.setIndex(g.indices)
  geo.computeVertexNormals()

  let glowGeo: THREE.BufferGeometry | null = null
  if (glow.positions.length > 0) {
    glowGeo = new THREE.BufferGeometry()
    glowGeo.setAttribute('position', new THREE.Float32BufferAttribute(glow.positions, 3))
    glowGeo.setAttribute('color', new THREE.Float32BufferAttribute(glow.colors, 3))
    glowGeo.setIndex(glow.indices)
    glowGeo.computeVertexNormals()
  }

  return { geometry: geo, glowGeometry: glowGeo, lampPositions: lamps.map(l => l.worldPos) }
}

// ─── Fixed light pool ───────────────────────────────────────────────
// Keep a constant number of real Three.js point lights. Each frame,
// assign them to the nearest lamp positions based on camera distance.
// Constant count avoids shader recompilation; unused lights get
// intensity=0 so they cost nothing.

const LIGHT_POOL_SIZE = 20
const LAMP_LIGHT_DISTANCE = 250
const LAMP_LIGHT_INTENSITY = 200
const LAMP_LIGHT_COLOR = 0xFFAA55

function LightPool({ allLampPositions, sunPosition }: { allLampPositions: THREE.Vector3[]; sunPosition: THREE.Vector3 }) {
  const lightsRef = useRef<THREE.PointLight[]>([])

  // Create stable pool of light objects once
  const pool = useMemo(() => {
    const lights: THREE.PointLight[] = []
    for (let i = 0; i < LIGHT_POOL_SIZE; i++) {
      const l = new THREE.PointLight(LAMP_LIGHT_COLOR, 0, LAMP_LIGHT_DISTANCE, 2)
      lights.push(l)
    }
    lightsRef.current = lights
    return lights
  }, [])

  // Scratch arrays for sorting — allocated once
  const sortBuf = useRef<{ idx: number; dist: number }[]>([])

  useFrame(({ camera }) => {
    const camPos = camera.position

    // Compute local sun height relative to camera position on the sphere
    const camLen = camPos.length()
    const h = camLen > 0
      ? (sunPosition.x * camPos.x + sunPosition.y * camPos.y + sunPosition.z * camPos.z) / camLen
      : sunPosition.y
    const day = calculateDayFactor(h)
    // Lamps on at night (day=0), off during day (day=1)
    const nightIntensity = LAMP_LIGHT_INTENSITY * (1 - day)

    const lamps = allLampPositions
    const lights = lightsRef.current

    // Build sort buffer
    if (sortBuf.current.length !== lamps.length) {
      sortBuf.current = lamps.map((_, idx) => ({ idx, dist: 0 }))
    }
    const buf = sortBuf.current
    for (let i = 0; i < lamps.length; i++) {
      buf[i].idx = i
      buf[i].dist = camPos.distanceToSquared(lamps[i])
    }

    // Partial sort: only need the top LIGHT_POOL_SIZE nearest
    const n = Math.min(LIGHT_POOL_SIZE, lamps.length)
    for (let i = 0; i < n; i++) {
      let minJ = i
      for (let j = i + 1; j < buf.length; j++) {
        if (buf[j].dist < buf[minJ].dist) minJ = j
      }
      if (minJ !== i) {
        const tmp = buf[i]
        buf[i] = buf[minJ]
        buf[minJ] = tmp
      }
    }

    // Assign nearest lamps to pool lights
    for (let i = 0; i < LIGHT_POOL_SIZE; i++) {
      const light = lights[i]
      if (i < n && nightIntensity > 0.01) {
        const pos = lamps[buf[i].idx]
        light.position.copy(pos)
        light.intensity = nightIntensity
      } else {
        light.intensity = 0
      }
    }
  })

  return (
    <>
      {pool.map((light, i) => (
        <primitive key={i} object={light} />
      ))}
    </>
  )
}

// ─── Main renderer ───────────────────────────────────────────────────

export function VillageRenderer({ maxVillages = 30, maxDocks = 40, sunPosition }: { maxVillages?: number; maxDocks?: number; sunPosition: THREE.Vector3 }) {
  const { meshDataList, allLampPositions } = useMemo(() => {
    const villages = findVillageSites(maxVillages)
    const docks = findDockSites(maxDocks)
    // Expose placements for interaction detection
    setVillagePlacements(villages, docks)
    const all = [...villages, ...docks]
    const dataList = all.map(buildVillageMeshData)
    const lamps = dataList.flatMap(d => d.lampPositions)
    return { meshDataList: dataList, allLampPositions: lamps }
  }, [maxVillages, maxDocks])

  return (
    <group>
      {meshDataList.map((data, vi) =>
        data.geometry ? (
          <group key={vi}>
            <mesh geometry={data.geometry}>
              <meshStandardMaterial vertexColors roughness={0.92} flatShading />
            </mesh>
            {data.glowGeometry && (
              <mesh geometry={data.glowGeometry} material={lanternMaterial} />
            )}
          </group>
        ) : null,
      )}
      <LightPool allLampPositions={allLampPositions} sunPosition={sunPosition} />
    </group>
  )
}
